import {
    KEAP_CUSTOM_LINK_TYPES,
    KEAP_TOOLS,
    SITE_STATUS,
    TEMPLATE_MEDIA,
    UNLAYER_DOMAIN,
    UNLAYER_ENV_URLS,
} from './unlayer.constants';
import { version } from '@infusionsoft/keap-unlayer-editor';
import clonedeep from 'lodash.clonedeep';
import { convert as slugify } from 'url-slug';
import {
    createTemplateGroup,
    getTemplate,
    getTemplateGroup,
    getGlobalTemplate,
    updateTemplate,
    updateTemplateGroup,
} from './marketingSiteEditorApi';
import { updateCustomFormsWithinPage } from './customForm.utils';
import { convertUnlayerFormsToKeapForms } from '../../unlayer/UnlayerFormConverters';
import { unlayerDesign } from '../../unlayer/UnlayerDesignProcessor';

import { mapSiteInputToGroupCreate } from './mappers';

const LINK_TYPE_ATTR = 'data-keap-link-type';
const LINK_VALUE_ATTR = 'data-keap-link';

const getEnvironmentUrl = (environment) => {
    const url = UNLAYER_ENV_URLS[environment?.toLowerCase()];

    return url || UNLAYER_ENV_URLS.default;
};

export function buildMarketingSiteUrl(environment, tenantId, alias, customDomain) {
    if (customDomain && (customDomain.dkimName && customDomain.verified)) {
        return `https://${customDomain.domainName}/${alias}.html`;
    }

    const environmentUrl = getEnvironmentUrl(environment);

    return `${environmentUrl}/${tenantId}/${alias}.html`;
}

export function customDomainPointsTo(currentEnv) {
    const environmentUrl = getEnvironmentUrl(currentEnv);

    return environmentUrl.replace('https://', 'pages.');
}

export function buildMarketingSiteEmbedCode(siteUrl) {
    return `<iframe src="${siteUrl}" style="border:none; min-height: 700px; width: 1px; min-width: 100%; *width: 100%;" name="site" frameborder="0" marginheight="0px" marginwidth="0px" width="100%" height="100%" referrerpolicy="unsafe-url" allowfullscreen></iframe>`;
}

export function buildMarketingPageUrl(environment, tenantId, siteSlug, pageSlug, hasCustomDomain) {
    if (hasCustomDomain) {
        return `/${siteSlug}/${pageSlug}.html`;
    }

    const environmentUrl = getEnvironmentUrl(environment);

    return `${environmentUrl}/${tenantId}/${siteSlug}/${pageSlug}.html`;
}


export function clearKeapFormsFromPages(pages) {
    return clonedeep(pages).map((page) => {
        page.builderJson.content = resetKeapForms(page.builderJson.content);

        return page;
    });
}

let formCount = 1;


export function updateFormId(html, design) {
    const forms = unlayerDesign(design).findByType(KEAP_TOOLS.keapForm);

    const root = document.createElement('div');

    let formModified = false;

    root.innerHTML = html;
    root.querySelectorAll('keap-form').forEach((htmlForm) => {
        forms.forEach(({ content }) => {
            const form = content?.values?.keapForm;
            const htmlFormSlug = htmlForm.getAttribute('slug');
            const htmlFormId = htmlForm.getAttribute('form-id');

            if (form.slug === htmlFormSlug && !htmlFormId) {
                formModified = true;
                htmlForm.setAttribute('form-id', form.id);
            }
        });
    });

    return formModified ? root.innerHTML : html;
}

function resetKeapForms(design) {
    const designHelper = unlayerDesign(design);

    designHelper.findByType(KEAP_TOOLS.keapForm)
        .forEach(({ content = {} }) => {
            const { values: { keapForm = {} } = {} } = content;

            keapForm.slug = `${Date.now()}-${formCount++}`;
            delete keapForm.id;
            delete keapForm.formId;
        });

    return designHelper.design;
}

export function parseHashParameters(hash) {
    return hash.split(';').reduce((obj, param) => {
        const [key, value] = param.split('=');

        obj[key] = value;

        return obj;
    }, {});
}

export function processAllUrlsInUnlayerDesign(design, generateNewUrl, site, appId, currentEnv, hasCustomDomain = false, enableFullUrlConversion = false) {
    unlayerDesign(design).allCells().forEach((cell) => {
        const { cellType, content: { values = {} } = {} } = cell;

        // eslint-disable-next-line no-unused-vars
        for (const [_, value] of Object.entries(values)) {
            if (value && typeof value === 'object' && value?.attrs?.href) {
                replaceUrlsInLinkProperty(value, generateNewUrl);
            }
        }

        if (cellType === 'text' || cellType === 'heading' || cellType === 'paragraph') {
            // In Unlayer 1.382.0+, heading and paragraph tools store content in textJson (Lexical format).
            if (values.textJson) {
                values.textJson = processLinksInTextJson(values.textJson, generateNewUrl);
            }

            if (values.text) {
                values.text = processAllUrlsInHtmlBlock(values?.text ?? '', generateNewUrl, hasCustomDomain);

                if (appId && site && currentEnv) {
                    values.text = regenerateAllULinkValues(values?.text ?? '', currentEnv, appId, site, enableFullUrlConversion, hasCustomDomain);
                }
            }
        } else if (cellType === 'menu') {
            values.menu.items?.forEach((item) => {
                if (item.link) {
                    replaceUrlsInLinkProperty(item.link, generateNewUrl);
                }
            });
        }
    });

    return design;
}

/**
 * Processes links within Lexical editor JSON format (used by heading cells in Unlayer 1.382.0+).
 * Recursively traverses the Lexical tree structure and updates marketing page links.
 */
function processLinksInTextJson(textJson, generateNewUrl) {
    if (!textJson || typeof textJson !== 'string') {
        return textJson;
    }

    try {
        const parsedTextJson = JSON.parse(textJson);

        updateLinksInTextJsonNode(parsedTextJson.root, generateNewUrl);

        return JSON.stringify(parsedTextJson);
    } catch (error) {
        return textJson;
    }
}

function updateLinksInTextJsonNode(node, generateNewUrl) {
    if (!node || typeof node !== 'object') {
        return;
    }

    if (node.type === 'link' || node.type === 'extended-link') {
        const linkType = node?.values?.name;

        if (node.url && linkType === KEAP_CUSTOM_LINK_TYPES.marketingPage) {
            const newUrl = generateNewUrl(node.url, linkType);

            if (newUrl) {
                node.url = newUrl;

                if (node.values?.values?.marketingPage) {
                    node.values.values.marketingPage = newUrl;
                }
            }
        }
    }

    if (Array.isArray(node.children)) {
        node.children.forEach((child) => updateLinksInTextJsonNode(child, generateNewUrl));
    }
}

function replaceUrlsInLinkProperty(linkData, generateNewUrl) {
    const { attrs = {}, values = {} } = linkData ?? {};
    const linkType = attrs[LINK_TYPE_ATTR];

    let newUrl;
    let oldUrl;

    if (linkType === KEAP_CUSTOM_LINK_TYPES.marketingPage) {
        oldUrl = values.marketingPage;

        newUrl = generateNewUrl(oldUrl, KEAP_CUSTOM_LINK_TYPES.marketingPage);

        if (newUrl) {
            values.marketingPage = newUrl;
        }
    }
}

export function convertUrlSlugsForClone(currentEnv, href, appId, site, oldPageToNewPageMap, hasCustomDomain) {
    const { id: newSiteRef, alias: siteAlias, templates } = site;
    const [url, hash = ''] = href?.split('#') ?? [];
    const { pageRef, siteRef } = parseHashParameters(hash);

    if (pageRef && siteRef) {
        const newPageRef = oldPageToNewPageMap[pageRef]?.id ?? site.templates.find((p) => p.alias === url.replace(/.*\//, '').replace(/\.html$/, ''))?.id;
        const currentPageSlug = templates.find((p) => p.id === newPageRef)?.alias;

        if (!currentPageSlug || !appId || !siteAlias) {
            return null;
        }
        const newUrl = `${buildMarketingPageUrl(currentEnv, appId, siteAlias, currentPageSlug, hasCustomDomain)}${buildHashParameters(appId, newSiteRef, newPageRef)}`;

        if (newUrl !== href || (hasCustomDomain && newUrl === href)) {
            return newUrl;
        }

        return null;
    }

    return null;
}

export function buildHashParameters(appId, siteId, pageId) {
    return `#appId=${appId};siteRef=${siteId};pageRef=${pageId}`;
}

export function mapMarketingSiteToCreateSiteInput(siteToCopy, newSiteTitle = siteToCopy.title, status) {
    const newSiteAlias = `${slugify(newSiteTitle.toLowerCase())}-${Date.now()}`;

    return {
        title: newSiteTitle,
        alias: newSiteAlias,
        builderJson: siteToCopy.builderJson,
        html: siteToCopy.html,
        templates: siteToCopy.templates,
        status: status || SITE_STATUS.DRAFT,
    };
}

export function clearThirdPartyLinks(pages, thirdPartyDomain) {
    return clonedeep(pages).map((page) => {
        page.builderJson.content = resetThirdPartyLinks(page.builderJson.content,
            thirdPartyDomain);

        if (page.html) {
            page.html = resetThirdPartyLinksInHtml(page.html, thirdPartyDomain);
        }

        return page;
    });
}

/**
 * Resets hrefs pointing at a third-party domain within an HTML string, including
 * Unlayer's base64-encoded data-u-link-value metadata when present.
 */
function resetThirdPartyLinksInHtml(html, thirdPartyDomain) {
    if (!html || typeof html !== 'string') {
        return html;
    }

    const root = document.createElement('div');

    root.innerHTML = html;
    root.querySelectorAll('a[href]').forEach((link) => {
        const href = link.getAttribute('href') ?? '';

        if (!href.toLowerCase().includes(thirdPartyDomain)) {
            return;
        }

        link.setAttribute('href', '#');

        const encodedLinkValue = link.getAttribute('data-u-link-value');

        if (!encodedLinkValue) {
            return;
        }

        try {
            const decoded = JSON.parse(atob(encodedLinkValue));
            const nestedValues = decoded?.values;

            if (nestedValues?.href?.toLowerCase()?.includes(thirdPartyDomain)) {
                nestedValues.href = '#';
            }

            if (nestedValues?.marketingPage?.toLowerCase()?.includes(thirdPartyDomain)) {
                nestedValues.marketingPage = '#';
            }

            link.setAttribute('data-u-link-value', btoa(JSON.stringify(decoded)));
        } catch (error) {
            // Leave the attribute alone if it isn't valid base64/JSON.
        }
    });

    return root.innerHTML;
}

/**
 * Resets links to a third-party domain within Lexical editor JSON format (used by
 * heading/paragraph cells in Unlayer 1.382.0+).
 * Recursively traverses the Lexical tree structure and blanks out matching links.
 */
function resetThirdPartyLinksInTextJson(textJson, thirdPartyDomain) {
    if (!textJson || typeof textJson !== 'string') {
        return textJson;
    }

    try {
        const parsedTextJson = JSON.parse(textJson);

        resetThirdPartyLinksInTextJsonNode(parsedTextJson.root, thirdPartyDomain);

        return JSON.stringify(parsedTextJson);
    } catch (error) {
        return textJson;
    }
}

function resetThirdPartyLinksInTextJsonNode(node, thirdPartyDomain) {
    if (!node || typeof node !== 'object') {
        return;
    }

    if (node.type === 'link' || node.type === 'extended-link') {
        const nestedValues = node.values?.values;
        const urlMatches = node.url?.toLowerCase()?.includes(thirdPartyDomain);
        const hrefMatches = nestedValues?.href?.toLowerCase()?.includes(thirdPartyDomain);
        const marketingPageMatches = nestedValues?.marketingPage?.toLowerCase()?.includes(thirdPartyDomain);

        if (urlMatches || hrefMatches || marketingPageMatches) {
            if (node.url) {
                node.url = '#';
            }

            if (nestedValues?.href) {
                nestedValues.href = '#';
            }

            if (nestedValues?.marketingPage) {
                nestedValues.marketingPage = '#';
            }
        }
    }

    if (Array.isArray(node.children)) {
        node.children.forEach((child) => resetThirdPartyLinksInTextJsonNode(child, thirdPartyDomain));
    }
}

export function resetThirdPartyLinks(design, thirdPartyDomain) {
    const designHelper = unlayerDesign(design);

    designHelper.allCells()
        .forEach((content) => {
            switch (content.cellType) {
            case 'menu':
                if (content.content?.values?.menu?.items) {
                    content.content?.values.menu.items.forEach((element) => {
                        if (element.link.values.href
                                && element.link.values.href.toLowerCase()?.includes(thirdPartyDomain)) {
                            element.link.values.href = '#';
                        }
                    });
                }
                break;
            case 'heading':
            case 'paragraph':
            case 'text':
                if (content.content?.values?.text) {
                    content.content.values.text = resetThirdPartyLinksInHtml(
                        content.content.values.text,
                        thirdPartyDomain,
                    );
                }

                if (content.content?.values?.textJson) {
                    content.content.values.textJson = resetThirdPartyLinksInTextJson(
                        content.content.values.textJson,
                        thirdPartyDomain,
                    );
                }
                break;
            case 'image':
                if (content.content?.values?.action?.values?.href
                        && content.content.values.action.values.href.toLowerCase()?.includes(thirdPartyDomain)) {
                    content.content.values.action.values.href = '#';
                }
                break;
            case 'social':
                if (content.content?.values?.icons?.icons) {
                    content.content.values.icons.icons.forEach((element) => {
                        if (element.url
                                && element.url.toLowerCase()?.includes(thirdPartyDomain)) {
                            element.url = '#';
                        }
                    });
                }
                break;
            default:
                if (content.content?.values?.href?.values?.href
                        && content.content.values.href.values.href.toLowerCase()?.includes(thirdPartyDomain)) {
                    content.content.values.href.values.href = '#';
                }
                break;
            }
        });

    return designHelper.design;
}

export function processAllUrlsInHtmlBlock(html, generateNewUrl, hasCustomDomain) {
    const root = document.createElement('div');

    root.innerHTML = html;
    root.querySelectorAll('a[data-keap-link-type]').forEach((link) => {
        const href = link.getAttribute('href');
        const linkType = link.getAttribute(LINK_TYPE_ATTR) ?? KEAP_CUSTOM_LINK_TYPES.marketingPage;
        const newUrl = generateNewUrl(href, linkType, hasCustomDomain);

        if (newUrl) {
            link.setAttribute('href', newUrl);
            link.setAttribute(LINK_VALUE_ATTR, newUrl);
        }
    });

    root.querySelectorAll('[success-redirect]').forEach((redirector) => {
        const href = redirector.getAttribute('success-redirect');
        const linkType = redirector.getAttribute(LINK_TYPE_ATTR) ?? KEAP_CUSTOM_LINK_TYPES.marketingPage;
        const newUrl = generateNewUrl(href, linkType, hasCustomDomain);

        if (newUrl) {
            redirector.setAttribute('success-redirect', newUrl);
        }
    });

    return root.innerHTML;
}

export function regenerateAllULinkValues(html, currentEnv, appId, site, enableFullUrlConversion = false, hasCustomDomain = false) {
    if (!html || typeof html !== 'string') {
        return html;
    }

    return html.replace(/<a\b[^>]*>/gi, (tag) => {
        if (!/data-keap-link\s*=/i.test(tag)) {
            return tag;
        }

        const getAttr = (name, fallback) => {
            const match = tag.match(new RegExp(`${name}\\s*=\\s*"(.*?)"`, 'i'));

            return match ? match[1] : fallback;
        };

        let keapLink = getAttr('data-keap-link', null);

        if (!keapLink) {
            return tag;
        }

        if (enableFullUrlConversion && keapLink.startsWith('/') && appId && site && currentEnv) {
            const match = keapLink.match(/^\/([^/]+)\/([^/]+)\.html/);

            if (match) {
                const [, siteSlug, pageSlug] = match;

                keapLink = buildMarketingPageUrl(currentEnv, appId, siteSlug, pageSlug, hasCustomDomain);
            }
        }

        const linkType = getAttr('data-keap-link-type', 'marketingPage');
        const target = getAttr('target', '_self');

        const obj = {
            name: linkType,
            attrs: {
                href: `{{${linkType}}}`,
                target,
                'data-keap-link-type': linkType,
            },
            values: {
                [linkType]: keapLink,
            },
        };

        const encoded = btoa(JSON.stringify(obj));

        if (/data-u-link-value\s*=/i.test(tag)) {
            return tag.replace(
                /data-u-link-value\s*=\s*"(.*?)"/i,
                `data-u-link-value="${encoded}"`,
            );
        }

        return tag.replace(/>$/, ` data-u-link-value="${encoded}">`);
    });
}

export function convertLinksInDesign(currentEnv, design, appId, site, oldPageToNewPageMap, hasCustomDomain, enableFullUrlConversion = false) {
    return processAllUrlsInUnlayerDesign(design, (url, urlType) => {
        if (urlType === KEAP_CUSTOM_LINK_TYPES.marketingPage) {
            return convertUrlSlugsForClone(currentEnv, url, appId, site, oldPageToNewPageMap, hasCustomDomain);
        }

        return null;
    }, site, appId, currentEnv, hasCustomDomain, enableFullUrlConversion);
}

export function convertLinksInHtml(currentEnv, html, appId, site, oldPageToNewPageMap, hasCustomDomain) {
    return processAllUrlsInHtmlBlock(html, (oldUrl, linkType, customDomain) => {
        if (linkType === KEAP_CUSTOM_LINK_TYPES.marketingPage) {
            return convertUrlSlugsForClone(currentEnv, oldUrl, appId, site, oldPageToNewPageMap, customDomain);
        }

        return null;
    }, hasCustomDomain);
}

/**
 * Generate a unique slug for the page.
 *
 * @param {MarketingSite} site
 * @param {MarketingPage} page
 */
export const generatePageSlug = (site) => {
    let aliasNumber = (site?.templates?.length ?? 0) + 1;
    const pageAliases = site.templates.map((page) => `${page.alias}`);

    while (pageAliases.includes(`${aliasNumber}`)) aliasNumber++;

    return `${aliasNumber}`;
};

export const BLANK_DESIGN = {
    location: { collection: 'bodies', id: '2' },
    counters: {
        u_column: 2,
        u_row: 2,
        u_content_image: 1,
    },
    body: {
        rows: [
            {
                cells: [
                    1,
                ],
                columns: [
                    {
                        contents: [],
                        values: {
                            backgroundColor: '',
                            padding: '0px',
                            border: {},
                            _meta: {
                                htmlID: 'u_column_2',
                                htmlClassNames: 'u_column',
                            },
                        },
                    },
                ],
                values: {
                    displayCondition: null,
                    columns: false,
                    backgroundColor: '',
                    columnsBackgroundColor: '',
                    backgroundImage: {
                        url: '',
                        fullWidth: true,
                        repeat: false,
                        center: true,
                        cover: false,
                    },
                    padding: '0px',
                    _meta: {
                        htmlID: 'u_row_2',
                        htmlClassNames: 'u_row',
                    },
                    selectable: true,
                    draggable: true,
                    duplicatable: true,
                    deletable: true,
                },
            },
        ],
        values: {
            backgroundColor: '#e7e7e7',
            backgroundImage: {
                url: '',
                fullWidth: true,
                repeat: false,
                center: true,
                cover: false,
            },
            contentWidth: '1024px',
            contentAlign: 'center',
            fontFamily: {
                label: 'Arial',
                value: 'arial,helvetica,sans-serif',
            },
            linkStyle: {
                body: true,
                linkColor: '#0000ee',
                linkHoverColor: '#0000ee',
                linkUnderline: true,
                linkHoverUnderline: true,
            },
            _meta: {
                htmlID: 'u_body',
                htmlClassNames: 'u_body',
            },
        },
    },
    schemaVersion: 5,
};

const UNLAYER_TOOLS_VERSION = version.substring(1);
const HOSTING_SCRIPT_URL = `https://storage.googleapis.com/is-unlayer-tools.appspot.com/builds/keap-hosting-${UNLAYER_TOOLS_VERSION}.js`;

export function buildPageHtml(currentEnv, appId, site, page, chunks, body) {
    if (!chunks) return null;
    const thankYouTemplate = site.templates.find((p) => (p.id !== site.mainTemplateId)) || site.templates[0];
    const thankYouSlug = thankYouTemplate.alias;
    const successAttribute = thankYouSlug ? `success-redirect="${site.alias}/${thankYouSlug}.html"` : '';
    const customFaviconUrl = faviconLinkGenerator(site.settings?.faviconUrl);
    const generatedTrackingScripts = generateTrackingScripts(site?.settings);

    let html = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="utf-8">
            <meta http-equiv="x-ua-compatible" content="ie=edge">
            <title>${site.title ?? ''}</title>
            ${generatedTrackingScripts}
            <meta name="description" content="${page.seoDescription ?? ''}">
            <meta name="viewport" content="width=device-width, initial-scale=1">`;

    chunks.fonts?.forEach((font) => {
        html += `
                <link href="${font?.url}" rel="stylesheet" type="text/css">
            `;
    });

    chunks.jsModules?.forEach((jsmod) => {
        if (jsmod.startsWith('<script')) html += jsmod;
        else if (jsmod) {
            const type = jsmod.endsWith('esm.js') ? 'module' : 'text/javascript';

            html += `  <script type="${type}" src="${jsmod}"></script>
`;
        }
    });

    if (chunks.css) {
        html += `
  <style>
    ${chunks.css}
  </style>`;
    }

    if (chunks.js) {
        html += `
  <script type="text/javascript">
    ${chunks.js}
  </script>
`;
    }

    html += `
${customFaviconUrl}
  <script src="${HOSTING_SCRIPT_URL}"></script>
</head>
<body>
    <keap-hosting
            app-id="${appId}"
            version="${UNLAYER_TOOLS_VERSION}"
            ${successAttribute}
            env="${currentEnv}">
        <keap-scope
                marketing-site-slug="${site.id}"
                marketing-site-name="${site.title}"
                marketing-page-slug="${page.alias}"
                marketing-page-name="${page.title}">
            <keap-event name="marketing-page-viewed">
                ${body || chunks.body}
            </keap-event>
        </keap-scope>
    </keap-hosting>
</body>
</html>`;

    return html;
}

export const generateTrackingScripts = (siteSettings) => {
    if (!siteSettings) return [];

    const {
        facebookTrackingEnabled = false,
        googleTrackingEnabled = false,
        facebookTrackingId = '',
        googleTrackingId = '',
    } = siteSettings;

    const trackingScripts = `
    ${googleTrackingEnabled && googleTrackingId ? `<script async src="https://www.googletagmanager.com/gtag/js?id=${googleTrackingId}"></script> 
    <script>window.dataLayer = window.dataLayer || [];
    function gtag(){
        dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${googleTrackingId}');
        </script>` : ''} 


    ${facebookTrackingEnabled && facebookTrackingId ? `<script>
    !function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function() {
        n.callMethod ?
            n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s)
    }(window, document, 'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${facebookTrackingId}');
    fbq('track', 'PageView');
    </script><noscript>
    <img height="1" width="1" style="display:none"src="https://www.facebook.com/tr?id=${facebookTrackingId}&ev=PageView&noscript=1"/></noscript>` : ''}
    `;


    return trackingScripts;
};

const faviconLinkGenerator = (faviconUrl) => {
    if (!faviconUrl) {
        return '';
    }

    const linkTags = `
        <link href=${faviconUrl} rel=mask-icon type=image/x-icon />
        <link href=${faviconUrl} rel=icon type=image/x-icon />
    `;

    return linkTags;
};

export const decodeHtml = (html) => {
    if (!html) return '';

    const textarea = document.createElement('textarea');

    textarea.innerHTML = html;

    const decodedString = textarea.value;

    return decodedString;
};


/**
     * This is horribly inefficient, but it was a non-negotiable requirement to have the form names auto-name in
     * ascending order based on the funnel/site instead of the page.  So, we have to read all the unlayer json design
     * documents, and look for all forms.  This method will have to suffice until we get the endpoint that lists the
     * linked forms complete.
*/

export const getTotalFormCount = async (site) => {
    const marketingPageContents = await Promise.all(
        site.templates.map(async (page) => {
            return (await getTemplate(page.id)).content;
        }),
    );

    const allForms = marketingPageContents.flatMap((content) => {
        if (content == null) return [];
        const { design } = content;

        return unlayerDesign(design).findByType(KEAP_TOOLS.keapForm);
    });

    return allForms.length;
};

const INPUT_TYPE_ADDRESS_GROUP = 'AddressGroup';
const INPUT_TYPE_CHECKBOX = 'Checkboxes';
const INPUT_TYPE_COMPANY = 'Company';
const INPUT_TYPE_CREDIT_CARD = 'CreditCard';
const INPUT_TYPE_CURRENCY = 'Currency';
const INPUT_TYPE_DATE = 'Date';
const INPUT_TYPE_DATE_TIME = 'DateTime';
const INPUT_TYPE_DECIMAL = 'Decimal';
const INPUT_TYPE_EMAIL = 'Email';
const INPUT_TYPE_MULTISELECT = 'MultiSelect';
const INPUT_TYPE_NUMBER = 'Number';
const INPUT_TYPE_PHONE = 'Phone';
const INPUT_TYPE_PHONE_GROUP = 'PhoneGroup';
const INPUT_TYPE_RADIO = 'Radio';
const INPUT_TYPE_SELECT = 'Select';
const INPUT_TYPE_TEXT = 'Text';
const INPUT_TYPE_TEXT_AREA = 'TextArea';
const INPUT_TYPE_URL = 'Url';
const INPUT_TYPE_YEAR = 'Year';
const INPUT_TYPE_YESNO = 'YesNo';
const INPUT_TYPE_DAY_OF_WEEK = 'DayOfWeek';
const INPUT_TYPE_MONTH = 'Month';
const INPUT_TYPE_WEBSITE = 'Website';
const INPUT_TYPE_NUMBER_DECIMAL = 'NumberDecimal';
const INPUT_TYPE_STATE = 'State';
const INPUT_TYPE_PERCENT = 'Percent';
const INPUT_OPTIONS_TYPE_COUNTRY = 'Country';

const CUSTOM_FIELD_TYPES_WITH_OPTIONS = [INPUT_TYPE_STATE, INPUT_TYPE_MONTH, INPUT_TYPE_DAY_OF_WEEK];

export const SAVED_SOURCE = 'saved';
export const GALLERY_SOURCE = 'stock';
export const PUBLISHED_SOURCE = 'published';

const UNLAYER_INPUT_TYPES = {
    DROPDOWN: 'dropdown',
    CHECKBOX: 'checkbox',
    BOOLEAN: 'boolean',
    TEXTAREA: 'textarea',
    DATE: 'date',
    NUMBER: 'number',
    RADIO: 'radio',
    TEXT: 'text',
    DATETIME: 'datetime',
    EMAIL: 'email',
    PHONE: 'tel',
    STATE: 'dropdown',
    DAY_OF_WEEK: 'dropdown',
    MONTH: 'dropdown',
};

/**
 * Maps keap custom input types to their corresponding unlayer types
 */
export const KEAP_INPUT_TYPE_MAPPING = {
    [INPUT_TYPE_SELECT]: UNLAYER_INPUT_TYPES.DROPDOWN,
    [INPUT_TYPE_MONTH]: UNLAYER_INPUT_TYPES.MONTH,
    [INPUT_TYPE_STATE]: UNLAYER_INPUT_TYPES.STATE,
    [INPUT_TYPE_WEBSITE]: UNLAYER_INPUT_TYPES.TEXT,
    [INPUT_TYPE_DAY_OF_WEEK]: UNLAYER_INPUT_TYPES.DAY_OF_WEEK,
    [INPUT_TYPE_RADIO]: UNLAYER_INPUT_TYPES.RADIO,
    [INPUT_TYPE_CHECKBOX]: UNLAYER_INPUT_TYPES.CHECKBOX,
    [INPUT_TYPE_TEXT]: UNLAYER_INPUT_TYPES.TEXT,
    [INPUT_TYPE_TEXT_AREA]: UNLAYER_INPUT_TYPES.TEXTAREA,
    [INPUT_TYPE_DATE]: UNLAYER_INPUT_TYPES.DATE,
    [INPUT_TYPE_DATE_TIME]: UNLAYER_INPUT_TYPES.DATETIME,
    [INPUT_TYPE_CURRENCY]: UNLAYER_INPUT_TYPES.NUMBER,
    [INPUT_TYPE_DECIMAL]: UNLAYER_INPUT_TYPES.NUMBER,
    [INPUT_TYPE_EMAIL]: UNLAYER_INPUT_TYPES.EMAIL,
    [INPUT_TYPE_MULTISELECT]: UNLAYER_INPUT_TYPES.CHECKBOX,
    [INPUT_TYPE_NUMBER]: UNLAYER_INPUT_TYPES.NUMBER,
    [INPUT_TYPE_PHONE]: UNLAYER_INPUT_TYPES.PHONE,
    [INPUT_TYPE_URL]: UNLAYER_INPUT_TYPES.TEXT,
    [INPUT_TYPE_YEAR]: UNLAYER_INPUT_TYPES.NUMBER,
    [INPUT_TYPE_YESNO]: UNLAYER_INPUT_TYPES.BOOLEAN,
    [INPUT_TYPE_NUMBER_DECIMAL]: UNLAYER_INPUT_TYPES.NUMBER,
    [INPUT_TYPE_PERCENT]: UNLAYER_INPUT_TYPES.NUMBER,
    // todo Implement all these
    [INPUT_OPTIONS_TYPE_COUNTRY]: null, // should be dropdown but we don't have it working in unlayer
    [INPUT_TYPE_CREDIT_CARD]: null,
    [INPUT_TYPE_COMPANY]: null,
    [INPUT_TYPE_PHONE_GROUP]: null,
    [INPUT_TYPE_ADDRESS_GROUP]: null,
};

export const customFieldTypeLabels = {
    Currency: 'Currency',
    Date: 'Date',
    DateTime: 'Date/Time',
    DayOfWeek: 'Day of Week',
    Select: 'Dropdown',
    NumberDecimal: 'Decimal Number',
    Email: 'Email',
    Month: 'Month',
    MultiSelect: 'Checkboxes',
    Percent: 'Percent',
    Phone: 'Phone Number',
    Radio: 'Radio Buttons',
    State: 'State',
    Text: 'Text',
    TextArea: 'Text Area',
    User: 'User',
    Website: 'Website',
    Number: 'Whole Number',
    Year: 'Year',
    YesNo: 'Yes/no',
};

export const REST_TO_SPA_CUSTOM_FIELD_TYPE_MAP = {
    ListBox: 'MultiSelect',
    Dropdown: 'Select',
    WholeNumber: 'Number',
    DecimalNumber: 'NumberDecimal',
    PhoneNumber: 'Phone',
};

export const convertCustomFieldToUnlayerField = (customField) => {
    const newType = REST_TO_SPA_CUSTOM_FIELD_TYPE_MAP[customField.fieldType];

    if (newType) {
        customField = { ...customField, fieldType: newType };
    }

    const mappedInputType = KEAP_INPUT_TYPE_MAPPING[customField.fieldType];

    if (!mappedInputType) { return null; }

    const { label: fieldLabel } = customField;
    const options = CUSTOM_FIELD_TYPES_WITH_OPTIONS.includes(customField.fieldType)
        ? customField.options
            ? customField.options.map(({ id, label }) => ({ value: id, label }))
            : []
        : customField.options ? customField.options.map((option) => option.id) : [];

    const name = `${customField.id}`;

    return {
        show_label: true,
        label: fieldLabel,
        name,
        type: mappedInputType,
        required: true,
        options,
        group: 'Custom fields',
        defaultToFirstOption: customField.defaultToFirstOption,
    };
};

export const getMarketingPage = async (id, source) => {
    if (source === GALLERY_SOURCE) return (await getGlobalTemplate(id)).data;

    return (await getTemplate(id)).data.templateDto;
};


export const loadFullSite = async (marketingSite, source) => {
    if (!marketingSite.templates) {
        marketingSite = await getTemplateGroup(marketingSite.id)?.data?.templateGroupDto;
    }

    const siteToCopy = clonedeep(marketingSite);

    siteToCopy.templates = await Promise.all(siteToCopy.templates.map(async ({ id }) => getMarketingPage(id, source)));

    return siteToCopy;
};

export const cloneSiteFromSite = async (marketingSite, siteName, tenantId, currentEnv, { source, saveAsTemplate = false } = {}) => {
    const fullyLoadedSite = await loadFullSite(marketingSite, source);

    return cloneSite(fullyLoadedSite, siteName, tenantId, currentEnv, { saveAsTemplate });
};

export const cloneSite = async (cloneSource, siteName, appId, currentEnv, { saveAsTemplate = false } = {}) => {
    const status = saveAsTemplate ? SITE_STATUS.SAVED : SITE_STATUS.DRAFT;
    const siteToCopy = {
        ...cloneSource,
        status,
        templates: clearKeapFormsFromPages(cloneSource.templates),
    };

    // This initial save is to generate the page IDs
    const firstSaveSite = await createSite(mapMarketingSiteToCreateSiteInput(siteToCopy, siteName, status), saveAsTemplate);

    const templateGroup = await clonePages(firstSaveSite, siteToCopy.templates, appId, currentEnv, { saveAsTemplate });

    await Promise.all(templateGroup.templates.map((template) => {
        const updatedTemplatePayload = {
            id: template.id,
            builderJson: template.builderJson,
            groupOrder: template.groupOrder,
            title: template.title,
            alias: template.alias,
            templateMedia: template.templateMedia,
            templateSource: template.templateSource,
            templateStatus: template.templateStatus,
            html: template.html,
            nonTransactionalSave: true,
        };

        return updateTemplate(templateGroup.id, template.id, updatedTemplatePayload);
    }));

    const updatedTemplateGroupPayload = {
        title: templateGroup.title,
        alias: templateGroup.alias,
        html: templateGroup.html,
        builderJson: templateGroup.builderJson,
        templateGroupStatus: saveAsTemplate ? SITE_STATUS.SAVED : SITE_STATUS.DRAFT,
        templateMedia: TEMPLATE_MEDIA.Web,
        mainTemplateId: templateGroup.mainTemplateId,
        globalTemplateId: cloneSource.globalTemplateId,
        globalTemplateStatus: cloneSource.globalTemplateStatus,
    };

    const { templateGroupDto: secondSaveSite } = (await updateTemplateGroup(templateGroup.id, updatedTemplateGroupPayload)).data;

    return secondSaveSite;
};


export const createSite = async (copiedSite, updateThumbnail = false) => {
    const newCreateTemplateGroup = mapSiteInputToGroupCreate(copiedSite);

    return createTemplateGroup(newCreateTemplateGroup, updateThumbnail);
};

export const clonePages = async (targetSite, sourceTemplates, appId, currentEnv, { saveAsTemplate = false } = {}) => {
    let preparedTemplates = clearKeapFormsFromPages(sourceTemplates);

    if (saveAsTemplate) {
        preparedTemplates = clearThirdPartyLinks(preparedTemplates, UNLAYER_DOMAIN);
    }

    // After pageIDs are generated, we need to replace all references to old page IDs

    return convertReferencesInClonedSite(currentEnv, preparedTemplates, targetSite, appId, false);
};

export const convertReferencesInClonedSite = async (currentEnv, originalPages, newSite, appId, hasCustomDomain = false) => {
    originalPages = clonedeep(originalPages);
    newSite = clonedeep(newSite);

    // Creates a mapping of
    // oldPageId -> newPage

    const oldPageIdToNewPageMap = originalPages.reduce((map, { alias: oldTemplateSlug, id: oldTemplateId }) => {
        map[oldTemplateId] = newSite.templates.find(({ alias }) => alias === oldTemplateSlug);

        return map;
    }, {});

    // For each page, we need to regenerate the html, and update the design as well.
    const templates = await Promise.all(originalPages.map(async (oldPage) => {
        const { id: oldTemplateId } = oldPage;
        const newPage = oldPageIdToNewPageMap[oldTemplateId];

        if (!newPage) {
            throw Error(`Can not find page map for old page id='${oldTemplateId}`);
        }


        const formNamePrefix = 'Lead Capture Form';
        const newDesign = await updateCustomFormsWithinPage({
            page: newPage,
            formNamePrefix: newSite.title,
            formNameGenerator: () => `${formNamePrefix} ${++formCount}`,
            design: convertUnlayerFormsToKeapForms(oldPage.builderJson.content),
        });

        newPage.builderJson.options = { ...newPage.builderJson.options };
        newPage.builderJson.options.chunks = newPage.builderJson.options.chunks ?? { ...newSite.builderJson.options.chunks, body: oldPage.html ?? newPage.html };
        newPage.builderJson.options.chunks.body = convertLinksInHtml(currentEnv, oldPage.html ?? newPage.html, appId, newSite, oldPageIdToNewPageMap, hasCustomDomain);
        newPage.html = buildPageHtml(currentEnv, appId, newSite, newPage, newPage.builderJson.options.chunks);
        newPage.builderJson.content = convertLinksInDesign(currentEnv, newDesign, appId, newSite, oldPageIdToNewPageMap, hasCustomDomain, false);

        return newPage;
    }));

    const {
        id,
        title,
        alias,
        status,
        createdDate,
        updatedDate,
        publishedDate,
        isDeleted,
        templateMedia,
        templateSource,
    } = newSite;

    return {
        id,
        title,
        alias,
        templates,
        mainTemplateId: templates[0].id,
        status,
        createdDate,
        updatedDate,
        publishedDate,
        isDeleted,
        builderJson: templates[0].builderJson,
        html: templates[0].html,
        templateMedia,
        templateSource,
    };
};


export const DEFAULT_LABELS = {
    'CustomFieldDetailsPage/parentName.contact': 'contacts',
    'CustomFieldEditAdvanced/contact.plural': 'contacts',
    'CustomFieldEditAdvanced/contact.singular': 'contact',
    'CustomFieldEditForm/recordTypes.contact': 'contact | contacts',
    'CustomFieldStatus/contact': 'contacts',
    'EditCustomFieldModal/parentName.contact': 'contacts',
    'SmsBroadcastRecipientSummary/contacts': 'contacts | contact | contacts',
    'contactDetails.fieldHeaders.billingAddress': 'Billing',
    'contactDetails.fieldHeaders.email': 'Work email',
    'contactDetails.fieldHeaders.email2': 'Personal email',
    'contactDetails.fieldHeaders.email3': 'Other email',
    'contactDetails.fieldHeaders.other': 'Fax',
    'contactDetails.fieldHeaders.otherAddress': 'Optional',
    'contactDetails.fieldHeaders.shippingAddress': 'Shipping',
    'contactDetails.sectionHeaders.addresses': 'Addresses',
    'contactDetails.sectionHeaders.phoneEmailFax': 'Phone, email, and fax',
    'contactDetails.view': 'View and edit info',
    'contactList.addContact.saveAndAdd': 'Save and add another',
    'contactList.addContact.title': 'Add a contact',
    'contactList.all.empty': 'No groups found containing "{search}"',
    'contactList.all.search': 'Search groups',
    'contactList.all.title': 'All groups',
    'contactList.applyTags.addremove': 'Choose a tag to add/remove',
    'contactList.applyTags.apply': 'Apply Tag',
    'contactList.applyTags.remove': 'Remove Tag',
    'contactList.applyTags.title': 'Add or remove tags',
    'contactList.automation.add': 'Add',
    'contactList.automation.button': 'Add to automation',
    'contactList.automation.selectCampaign': 'Select an automation',
    'contactList.automation.selectSequence': 'Select a Sequence',
    'contactList.automation.title': 'Add to an Automation',
    'contactList.back': 'Back to groups',
    'contactList.broadcast.send': 'Send a broadcast',
    'contactList.buttons.add': 'Add a contact',
    'contactList.buttons.create': 'Create a group',
    'contactList.buttons.filter': 'Filter',
    'contactList.buttons.import': 'Import',
    'contactList.companies.searchCompanies': 'Search companies',
    'contactList.companies.searchHelp': "Can't find a company name? Try searching.",
    'contactList.contactTypeAction.title': 'Change contact type',
    'contactList.deleteContacts.message': "If you delete this contact, you'll lose all data associated with them. That includes records, reports, and revenue history. Before deleting, consider downloading any necessary data for your personal files. | If you delete these contacts, you'll lose all data associated with them. That includes records, reports, and revenue history. Before deleting, consider downloading any necessary data for your personal files.",
    'contactList.deleteContacts.title': 'Delete this contact? | Delete these contacts?',
    'contactList.empty.addContactPrompt': 'Add "{name}" now',
    'contactList.empty.clearSearch': 'Clear your search',
    'contactList.empty.noResults': 'You have no contacts that match the search "{query}"',
    'contactList.filtering.add': 'Add a filter',
    'contactList.filtering.listTitle': 'Filtered Contacts',
    'contactList.filtering.save': 'Save group',
    'contactList.filtering.sort': 'Sort contacts by',
    'contactList.filtering.submit': 'View contacts',
    'contactList.headers.contacts': 'Contacts',
    'contactList.headers.filtered': 'Filtered',
    'contactList.lists.delete.action': 'Remove this group',
    'contactList.lists.delete.confirm': 'Only the group will be removed',
    'contactList.lists.delete.confirmMessage': 'Removing this group will not remove the contacts within the group.',
    'contactList.lists.groups': 'Groups',
    'contactList.lists.more': '<span class="number subtle">{number}</span> more group | <span class="number subtle">{number}</span> more groups',
    'contactList.lists.title': 'Groups',
    'contactList.lists.viewAll': 'View/hide groups',
    'contactList.newContacts.title': 'New contacts',
    'contactList.people': 'People',
    'contactList.stats.new': 'New today',
    'contactList.stats.total': 'Total',
    'contactList.tableGrid.dateAdded': 'Date added',
    'contactList.tableGrid.email': 'Email',
    'contactList.tableGrid.leadSource': 'Lead source',
    'contactList.tableGrid.name': 'Name',
    'contactList.takeAction': 'Take action',
    'contactList.title': 'All contacts',
    'contactList.toggleVisibility': 'Toggle group visibility',
    'contactList.updated': 'Updated yesterday',
    'contactList.viewContact': 'View Contact',
    'contactNote.addNote': 'Add note',
    'contactRecord.activities': 'Contact activity',
    'contactRecord.deals.header': 'Pipeline deals',
    'contactRecord.deals.noDate': 'No close date',
    'contactRecord.delete.action': 'Delete contact',
    'contactRecord.delete.confirm': "If you delete this contact, you'll lose all data associated with them. That includes records, reports, and revenue history. Before deleting, consider downloading any necessary data for your personal files.",
    'contactRecord.delete.success': 'Successfully deleted the contact',
    'contactRecord.email.header': 'Send an email',
    'contactRecord.email.label.from': 'From:',
    'contactRecord.email.label.recipients': 'Recipients:',
    'contactRecord.email.label.subject': 'Subject:',
    'contactRecord.email.label.to': 'To:',
    'contactRecord.email.placeholder.message': 'Add your message',
    'contactRecord.email.placeholder.subject': 'Add a subject',
    'contactRecord.email.users': 'Users',
    'contactRecord.labels.activity': 'Activity',
    'contactRecord.labels.appts': 'Meet',
    'contactRecord.labels.details': 'Details',
    'contactRecord.labels.email': 'Email',
    'contactRecord.labels.files': 'Files',
    'contactRecord.labels.forms': 'Forms',
    'contactRecord.labels.messages': 'Messages',
    'contactRecord.labels.more': 'More',
    'contactRecord.labels.note': 'Note',
    'contactRecord.labels.notes': 'Notes',
    'contactRecord.labels.sales': 'Sales',
    'contactRecord.labels.sms': 'Text',
    'contactRecord.labels.tag': 'Tag',
    'contactRecord.labels.tags': 'Tags',
    'contactRecord.labels.task': 'Task',
    'contactRecord.labels.texts': 'Texts',
    'contactRecord.notes.created': 'created by',
    'contactRecord.notes.note': 'Note',
    'contactRecord.notes.save': 'Save note',
    'contactRecord.notes.title': 'Notes',
    'contactRecord.pages.appointment.allDay': 'All day',
    'contactRecord.pages.appointment.description': 'Description',
    'contactRecord.pages.appointment.edit': 'Edit an appointment',
    'contactRecord.pages.appointment.location': 'Location',
    'contactRecord.pages.appointment.new': 'Add new appointment',
    'contactRecord.pages.appointment.title': 'Title',
    'contactRecord.pages.automatedMessages.activityCardTitle': 'Text Messages (Marketing Number)',
    'contactRecord.pages.automatedMessages.sidePanelTitle': 'Marketing Number',
    'contactRecord.pages.emails.modalTitle': 'Emails',
    'contactRecord.pages.emails.sidePanelTitle': 'Emails',
    'contactRecord.pages.emails.title': 'Email',
    'contactRecord.pages.files.modalTitle': 'Files',
    'contactRecord.pages.files.sidePanelTitle': 'Files',
    'contactRecord.pages.files.title': 'Files',
    'contactRecord.pages.messages.activityCardTitle': 'Text Messages (Business Line)',
    'contactRecord.pages.messages.sidePanelTitle': 'Business Line',
    'contactRecord.pages.money.modalTitle': 'Sales',
    'contactRecord.pages.money.sidePanelTitle': 'Sales',
    'contactRecord.pages.money.title': 'Sales',
    'contactRecord.pages.note.body': 'Add a note here...',
    'contactRecord.pages.note.edit': 'Edit note',
    'contactRecord.pages.note.new': 'Add a note',
    'contactRecord.pages.notes.modalTitle': 'Notes',
    'contactRecord.pages.notes.sidePanelTitle': 'Notes',
    'contactRecord.pages.notes.title': 'Notes',
    'contactRecord.pages.pipeline.sidePanelTitle': 'Pipeline',
    'contactRecord.pages.tags.modalTitle': 'Add tags',
    'contactRecord.pages.tags.sidePanelTitle': 'Tags',
    'contactRecord.pages.tags.title': 'Tags',
    'contactRecord.pages.task.addTask': 'Add a task',
    'contactRecord.pages.task.assignedTo': 'Assigned user',
    'contactRecord.pages.task.associatedContact': 'Associated contact',
    'contactRecord.pages.task.due': 'Due',
    'contactRecord.pages.task.dueDate': 'Due date',
    'contactRecord.pages.task.edit': 'Task details',
    'contactRecord.pages.task.hide': 'Hide completed',
    'contactRecord.pages.task.noneCompleted': 'None completed',
    'contactRecord.pages.task.show': 'Show completed',
    'contactRecord.pages.task.taskDate': 'Due date',
    'contactRecord.pages.task.taskNote': 'Note',
    'contactRecord.pages.task.taskTime': 'Time',
    'contactRecord.pages.task.taskTitle': 'Task title',
    'contactRecord.pages.task.toggle': 'Toggle completed tasks',
    'contactRecord.sales.addPaymentModalContent.add': 'Add',
    'contactRecord.sales.addPaymentModalContent.addProduct': 'Add a product',
    'contactRecord.sales.addPaymentModalContent.cancel': 'Cancel',
    'contactRecord.sales.addPaymentModalContent.cash': 'Cash',
    'contactRecord.sales.addPaymentModalContent.chargeStatement': "{name}'s card will be charged {amount}.",
    'contactRecord.sales.addPaymentModalContent.check': 'Check',
    'contactRecord.sales.addPaymentModalContent.country.options.USA': 'United States',
    'contactRecord.sales.addPaymentModalContent.country.placeholder': 'Choose your country',
    'contactRecord.sales.addPaymentModalContent.creditCard': 'Credit card',
    'contactRecord.sales.addPaymentModalContent.creditCardCVV': 'CVV',
    'contactRecord.sales.addPaymentModalContent.creditCardExpiration': 'Expiration',
    'contactRecord.sales.addPaymentModalContent.creditCardName': 'Name on card',
    'contactRecord.sales.addPaymentModalContent.creditCardNumber': 'Card number',
    'contactRecord.sales.addPaymentModalContent.currencyType': 'USD',
    'contactRecord.sales.addPaymentModalContent.isRecurringBilling': 'Recurring payment',
    'contactRecord.sales.addPaymentModalContent.paymentAmount': 'Payment amount',
    'contactRecord.sales.addPaymentModalContent.paymentFromLabel': 'Payment from',
    'contactRecord.sales.addPaymentModalContent.paymentReceivedLabel': 'Payment received on',
    'contactRecord.sales.addPaymentModalContent.paymentReferenceLabel': 'Payment reference',
    'contactRecord.sales.addPaymentModalContent.paymentType': 'Payment type',
    'contactRecord.sales.addPaymentModalContent.products': 'Products',
    'contactRecord.sales.addPaymentModalContent.selectProduct': 'Select a product',
    'contactRecord.sales.addPaymentModalContent.subscriptionStatement': "{name}'s card will be charged {amount} {recurringOption} starting {date} and ending after {occurrences} {occurrenceString}. Final charge happens on {endDate}.",
    'contactRecord.sales.addPaymentModalContent.subscriptionStatementNoEnding': "{name}'s card will be charged {amount} {recurringOption} starting {date}.",
    'contactRecord.sales.addPaymentModalContent.subscriptionUpdateStatement': '{name} has {occurrencesLeft} remaining {charge} for product {productName}. Final payment is set for {endDate}.',
    'contactRecord.sales.addPaymentModalContent.subscriptionUpdateStatementNoEnding': '{name} is paying for product {productName} {recurringOption}.',
    'contactRecord.sales.addPaymentModalContent.title': 'Add a payment',
    'contactRecord.sales.payments.addAPayment': 'Add a payment',
    'contactRecord.sales.payments.credit': 'Credit for refund',
    'contactRecord.sales.payments.creditCardPayment': 'Credit card payment made',
    'contactRecord.sales.payments.creditTo': 'Credit to',
    'contactRecord.sales.payments.empty': 'As you receive or record new payments they will appear here',
    'contactRecord.sales.payments.header': 'Payments',
    'contactRecord.sales.payments.manualPayment': 'Manual payment made',
    'contactRecord.sales.payments.paymentFrom': 'Payment from',
    'contactRecord.sales.payments.paypalAdjustment': 'PayPal payment failed',
    'contactRecord.sales.payments.paypalPayment': 'PayPal payment made',
    'contactRecord.sales.payments.recurring.cancelAction': 'Yes, cancel',
    'contactRecord.sales.payments.recurring.cancelDescription': 'Are you sure you want to cancel future recurring payments?',
    'contactRecord.sales.payments.recurring.cancelError': "Something went wrong and we couldn't cancel the recurring payment. Please try again.",
    'contactRecord.sales.payments.recurring.cancelLink': 'Cancel future recurring payments',
    'contactRecord.sales.payments.recurring.cancelSuccess': 'Recurring payments cancelled.',
    'contactRecord.sales.payments.recurring.cancelTitle': 'Cancel recurring payments',
    'contactRecord.sales.payments.recurring.durations.days': 'day | days',
    'contactRecord.sales.payments.recurring.durations.months': 'month | months',
    'contactRecord.sales.payments.recurring.durations.weeks': 'week | weeks',
    'contactRecord.sales.payments.recurring.durations.years': 'year | years',
    'contactRecord.sales.payments.recurring.options.daily': 'Daily',
    'contactRecord.sales.payments.recurring.options.monthly': 'Monthly',
    'contactRecord.sales.payments.recurring.options.weekly': 'Weekly',
    'contactRecord.sales.payments.recurring.options.yearly': 'Yearly',
    'contactRecord.sales.payments.recurring.priceCycleEnding.daily': '/day',
    'contactRecord.sales.payments.recurring.priceCycleEnding.monthly': '/mo',
    'contactRecord.sales.payments.recurring.priceCycleEnding.weekly': '/wk',
    'contactRecord.sales.payments.recurring.priceCycleEnding.yearly': '/yr',
    'contactRecord.sales.payments.recurring.productDurationDescription': 'for {duration}',
    'contactRecord.sales.payments.refund': 'Refund',
    'contactRecord.sales.payments.refundConfirmationAction': 'Yes, refund',
    'contactRecord.sales.payments.refundConfirmationMessage': 'Are you sure you want to refund this payment?',
    'contactRecord.sales.payments.refundConfirmationTitle': 'Refund payment',
    'contactRecord.sales.payments.refundPaymentError': "Something went wrong and we couldn't refund your order. Please try again.",
    'contactRecord.sales.payments.refundTo': 'Refund to',
    'contactRecord.sales.payments.request': 'Request Money',
    'contactRecord.sales.payments.showLess': 'Show less',
    'contactRecord.sales.payments.showMore': 'Show more',
    'contactRecord.sales.payments.title': 'Payment Description',
    'contactRecord.sales.payments.totalPaid': 'Total paid',
    'contactRecord.sales.payments.totalProjected': 'Total Projected',
    'contactRecord.sales.purchased.date': 'Date',
    'contactRecord.sales.purchased.header': 'Products purchased',
    'contactRecord.sales.purchased.product': 'Product',
    'contactRecord.sales.purchased.quantity': 'Quantity',
    'contactRecord.sales.purchased.totalCost': 'Total cost',
    'contactRecord.sales.purchased.unitCost': 'Unit cost',
    'contactRecord.tags.applyTag.button': 'Add tag to {name} | Add tag to {name} | Add tags to {name}',
    'contactRecord.tags.applyTag.buttonNoName': 'Add tag | Add tag | Add tags',
    'contactRecord.tags.applyTag.placeholder': 'Add new or existing tag...',
    'contactRecord.tags.category': 'Category',
    'contactRecord.tags.dateApplied': 'Date applied',
    'contactRecord.tags.manageAll': 'Manage all tags',
    'contactRecord.tags.search': 'Search tags',
    'contactRecord.tags.searchEmpty': 'No tags found containing "{search}"',
    'contactRecord.tags.tag': 'Tag',
    'contactRecord.tags.tagsApplied': '<span class="number small">{count}</span> Tag added to {name} | <span class="number small">{count}</span> Tags added to {name}',
    'contactRecord.title': 'Contact Record',
    'contactRecord.tooltips.disabled.call': 'No phone numbers',
    'contactRecord.tooltips.disabled.email': 'No email address',
    'contactRecord.tooltips.disabled.sms': 'No mobile numbers',
    'contactUs.title': 'Contact us',
    'contactsPage.title': 'Contacts',
    'forms.addAsCustomField': 'Add as custom field',
    'forms.address1': 'Address 1',
    'forms.address2': 'Address 2',
    'forms.address2Street1': 'Shipping address line 1',
    'forms.address2Street2': 'Shipping address line 2',
    'forms.address3Street1': 'Optional address line 1',
    'forms.address3Street2': 'Optional address line 2',
    'forms.anniversary': 'Anniversary',
    'forms.anniversaryMonth': 'Anniversary month',
    'forms.anniversaryYear': 'Anniversary year',
    'forms.assistantName': 'Assistant name',
    'forms.assistantPhone': 'Assistant phone',
    'forms.billingAddressCountry': 'Billing address country',
    'forms.billingAddressLine1': 'Billing address line 1',
    'forms.billingAddressLine2': 'Billing address line 2',
    'forms.billingAddressLocality': 'Billing address city',
    'forms.billingAddressPostalCode': 'Billing address postal code',
    'forms.billingAddressRegion': 'Billing address state',
    'forms.billingAddressStreet': 'Billing address street',
    'forms.birthday': 'Birthday',
    'forms.birthdayMonth': 'Birthday month',
    'forms.birthdayYear': 'Birthday year',
    'forms.business': 'Business name',
    'forms.businessEmail': 'Business email',
    'forms.city': 'Billing address city',
    'forms.city2': 'Shipping address city',
    'forms.city3': 'Optional address city',
    'forms.confirmPassword': 'Confirm password',
    'forms.contactType': 'Contact type (Lead, Client, Other)',
    'forms.country': 'Billing address country',
    'forms.country2': 'Shipping address country',
    'forms.country3': 'Optional address country',
    'forms.countryCode': 'Country',
    'forms.dateCreated': 'Contact added to {productName}',
    'forms.daveRamsey': 'Dave Ramsey/EntreLeadership',
    'forms.defaultApplicationTimeZone': 'Default application timezone',
    'forms.doNotImport': 'Do not import',
    'forms.email': 'Email',
    'forms.email2': 'Email 2',
    'forms.email3': 'Email 3',
    'forms.emailAddress2': 'Email address 2',
    'forms.emailAddress3': 'Email address 3',
    'forms.extension': 'Ext',
    'forms.facebook': 'Facebook',
    'forms.fallbackViewLocale': 'Default application language',
    'forms.familyName': 'Last name',
    'forms.fax': 'Fax',
    'forms.fax1': 'Fax',
    'forms.fax1Type': 'Fax 1 type',
    'forms.fax2': 'Fax 2',
    'forms.fax2Type': 'Fax 2 type',
    'forms.filters.contact': 'Contact fields',
    'forms.filters.custom': 'Custom fields',
    'forms.filters.event': 'Events',
    'forms.filters.other': 'Other fields',
    'forms.firstName': 'First name',
    'forms.friend': 'Friend',
    'forms.fullName': 'Full name',
    'forms.givenName': 'First name',
    'forms.google': 'Google',
    'forms.instagram': 'Instagram',
    'forms.jobTitle': 'Job title',
    'forms.language': 'Language',
    'forms.lastEmailed': 'Sent these contacts an email',
    'forms.lastName': 'Last name',
    'forms.lastUpdated': 'Last updated',
    'forms.leadScore': 'Lead score',
    'forms.leadSource': 'Lead source',
    'forms.leadscore': 'Lead score',
    'forms.linkedin': 'LinkedIn',
    'forms.locale': 'Locale',
    'forms.locality': 'City',
    'forms.middleName': 'Middle name',
    'forms.modifiers.after': 'After',
    'forms.modifiers.anytime': 'Anytime',
    'forms.modifiers.before': 'Before',
    'forms.modifiers.contains': 'Contains',
    'forms.modifiers.empty': 'Is empty',
    'forms.modifiers.endswith': 'Ends with',
    'forms.modifiers.equals': 'Equals',
    'forms.modifiers.exactlydaysago': 'Exactly (days ago)',
    'forms.modifiers.excludesall': 'Excludes all',
    'forms.modifiers.excludesany': 'Excludes any',
    'forms.modifiers.filled': 'Is filled',
    'forms.modifiers.fromdate': 'From date',
    'forms.modifiers.includesall': 'Includes all',
    'forms.modifiers.includesany': 'Includes any',
    'forms.modifiers.lessthandaysago': 'Less than (days ago)',
    'forms.modifiers.morethandaysago': 'More than (days ago)',
    'forms.modifiers.never': 'Never',
    'forms.modifiers.notcontains': 'Does not contain',
    'forms.modifiers.notequals': 'Not equals',
    'forms.modifiers.on': 'On',
    'forms.modifiers.please': 'Please select one',
    'forms.modifiers.range': 'Between',
    'forms.modifiers.startswith': 'Starts with',
    'forms.modifiers.todate': 'To date',
    'forms.name': 'Name',
    'forms.nickname': 'Nickname',
    'forms.originDate': 'IP Address Captured On',
    'forms.originIpAddress': 'IP Address',
    'forms.other': 'Other',
    'forms.otherAddressCountry': 'Other address country',
    'forms.otherAddressLine1': 'Other address line 1',
    'forms.otherAddressLine2': 'Other address line 2',
    'forms.otherAddressLocality': 'Other address city',
    'forms.otherAddressPostalCode': 'Other address postal code',
    'forms.otherAddressRegion': 'Other address state',
    'forms.otherAddressStreet': 'Other address street',
    'forms.owner': 'Owner',
    'forms.partner': '{productName} Partner',
    'forms.password': 'Password',
    'forms.phone': 'Phone',
    'forms.phone1': 'Phone 1',
    'forms.phone1Ext': 'Phone 1 ext',
    'forms.phone1Type': 'Phone 1 type',
    'forms.phone2': 'Phone 2',
    'forms.phone2Ext': 'Phone 2 ext',
    'forms.phone2Type': 'Phone 2 type',
    'forms.phone3': 'Phone 3',
    'forms.phone3Type': 'Phone 3 type',
    'forms.phone4': 'Phone 4',
    'forms.phone4Type': 'Phone 4 type',
    'forms.phone5': 'Phone 5',
    'forms.phone5Type': 'Phone 5 type',
    'forms.phoneNumber': 'Phone number',
    'forms.phoneType': 'Phone type',
    'forms.postalCode': 'Billing address postal code',
    'forms.postalCode2': 'Shipping address postal code',
    'forms.postalCode3': 'Optional address postal code',
    'forms.postalCodeGeneric': 'Postal code',
    'forms.region': 'State',
    'forms.reviewSite': 'Review Site',
    'forms.role': 'Role',
    'forms.selectAnOption': 'Select an option',
    'forms.shippingAddressCountry': 'Shipping address country',
    'forms.shippingAddressLine1': 'Shipping address line 1',
    'forms.shippingAddressLine2': 'Shipping address line 2',
    'forms.shippingAddressLocality': 'Shipping address city',
    'forms.shippingAddressPostalCode': 'Shipping address postal code',
    'forms.shippingAddressRegion': 'Shipping address state',
    'forms.shippingAddressStreet': 'Shipping address street',
    'forms.spouseName': 'Spouse name',
    'forms.ssn': 'SSN',
    'forms.standardFields.ADDRESS2_STREET1': 'Shipping address',
    'forms.standardFields.ADDRESS2_STREET2': 'Shipping address 2',
    'forms.standardFields.ADDRESS3_STREET1': 'Optional address',
    'forms.standardFields.ADDRESS3_STREET2': 'Optional address 2',
    'forms.standardFields.ANNIVERSARY': 'Anniversary',
    'forms.standardFields.BIRTHDAY': 'Birthday',
    'forms.standardFields.CITY': 'City',
    'forms.standardFields.CITY2': 'City',
    'forms.standardFields.CITY3': 'City',
    'forms.standardFields.CONTACT_TYPE': 'Contact type',
    'forms.standardFields.COUNTRY': 'Country',
    'forms.standardFields.COUNTRY2': 'Country',
    'forms.standardFields.COUNTRY3': 'Country',
    'forms.standardFields.EMAIL': 'Email address',
    'forms.standardFields.EMAIL_ADDRESS2': 'Email address 2',
    'forms.standardFields.EMAIL_ADDRESS3': 'Email address 3',
    'forms.standardFields.FACEBOOK': 'Facebook',
    'forms.standardFields.FAX1': 'Fax',
    'forms.standardFields.FAX1_TYPE': 'Fax type',
    'forms.standardFields.FAX2': 'Fax 2',
    'forms.standardFields.FAX2_TYPE': 'Fax 2 type',
    'forms.standardFields.FIRST_NAME': 'First name',
    'forms.standardFields.FORMS_NOTE': 'Add a note here...',
    'forms.standardFields.JOB_TITLE': 'Job title',
    'forms.standardFields.LAST_NAME': 'Last name',
    'forms.standardFields.LINKEDIN': 'LinkedIn',
    'forms.standardFields.MIDDLE_NAME': 'Middle name',
    'forms.standardFields.NICKNAME': 'Nickname',
    'forms.standardFields.OWNER': 'Owner',
    'forms.standardFields.PHONE1': 'Phone',
    'forms.standardFields.PHONE1_TYPE': 'Phone type',
    'forms.standardFields.PHONE2': 'Phone 2',
    'forms.standardFields.PHONE2_TYPE': 'Phone 2 type',
    'forms.standardFields.PHONE3': 'Phone 3',
    'forms.standardFields.PHONE3_TYPE': 'Phone 3 type',
    'forms.standardFields.PHONE4': 'Phone 4',
    'forms.standardFields.PHONE4_TYPE': 'Phone 4 type',
    'forms.standardFields.PHONE5': 'Phone 5',
    'forms.standardFields.PHONE5_TYPE': 'Phone 5 type',
    'forms.standardFields.POSTAL_CODE': 'Postal code',
    'forms.standardFields.POSTAL_CODE2': 'Postal code',
    'forms.standardFields.POSTAL_CODE3': 'Postal code',
    'forms.standardFields.SPOUSE_NAME': 'Spouse name',
    'forms.standardFields.STATE': 'State',
    'forms.standardFields.STATE2': 'State',
    'forms.standardFields.STATE3': 'State',
    'forms.standardFields.STREET_ADDRESS1': 'Billing address',
    'forms.standardFields.STREET_ADDRESS2': 'Billing address 2',
    'forms.standardFields.SUFFIX': 'Suffix',
    'forms.standardFields.TITLE': 'Title',
    'forms.standardFields.TWITTER': 'Twitter',
    'forms.standardFields.WEBSITE': 'Website',
    'forms.state': 'Billing address state',
    'forms.state2': 'Shipping address state',
    'forms.state3': 'Optional address state',
    'forms.street1': 'Street address 1',
    'forms.street2': 'Street address 2',
    'forms.streetAddress1': 'Billing address line 1',
    'forms.streetAddress2': 'Billing address line 2',
    'forms.suffix': 'Suffix',
    'forms.tag': 'Tags: Create tags from field values',
    'forms.tags': 'Tags',
    'forms.timeZone': 'Time zone',
    'forms.title': 'Title',
    'forms.twitter': 'Twitter',
    'forms.type': 'Type',
    'forms.uncategorized': 'Uncategorized',
    'forms.username': 'Username',
    'forms.website': 'Website',
    'forms.youtube': 'YouTube',
    'unlayer.customLinks.appointmentType.field.label': 'Page',
    'unlayer.customLinks.appointmentType.field.placeholder': 'Select page',
    'unlayer.customLinks.appointmentType.label': 'Go to booking page',
    'unlayer.customLinks.checkoutForm.field.label': 'Form',
    'unlayer.customLinks.checkoutForm.field.placeholder': 'Select form',
    'unlayer.customLinks.checkoutForm.label': 'Go to checkout form',
    'unlayer.customLinks.marketingPage.field.label': 'Page',
    'unlayer.customLinks.marketingPage.field.placeholder': 'Select page',
    'unlayer.customLinks.marketingPage.label': 'Go to landing sub-page',
    'unlayer.customLinks.marketingSite.field.label': 'Page',
    'unlayer.customLinks.marketingSite.field.placeholder': 'Select page',
    'unlayer.customLinks.marketingSite.label': 'Go to landing page',
    'unlayer.mergeFieldLabels.billingAddress': 'Billing Address',
    'unlayer.mergeFieldLabels.billingAddressExtended': 'Apt/Suite/Other',
    'unlayer.pagesPanel.edit.pageName.label': 'Page Name',
    'unlayer.pagesPanel.edit.seoDescription.label': 'SEO Description',
    'unlayer.pagesPanel.edit.seoDescription.placeholder': 'Describe the contents of this page',
    'unlayer.pagesPanel.panelName': 'Pages',
    'unlayer.pagesPanel.settings.label': 'Page Settings',
    'unlayer.pagesPanel.visiblePageHeader': 'Visible Pages',
    'unlayer.placeholder.appointmentType': 'Choose a link',
    'unlayer.placeholder.checkoutForm': 'Choose a form',
    'unlayer.placeholder.default': 'Choose one',
    'unlayer.settingsPanel.analytics.facebook.label': 'Facebook Pixel ID',
    'unlayer.settingsPanel.analytics.facebook.placeholder': 'Facebook Pixel ID',
    'unlayer.settingsPanel.analytics.facebook.validation': 'Use a valid ID, e.g., ID12345678',
    'unlayer.settingsPanel.analytics.google.label': 'Google Analytics Tracking ID',
    'unlayer.settingsPanel.analytics.google.placeholder': 'Google Analytics Tracking ID',
    'unlayer.settingsPanel.analytics.google.validation': 'Use a valid ID, e.g., UA-12345678-1',
    'unlayer.settingsPanel.analytics.toggle.label': 'Enable Analytics',
    'unlayer.settingsPanel.help.label': 'How do I get this?',
    'unlayer.settingsPanel.panelName': 'Settings',
    'unlayer.tool.bookingForm.select.label': 'Booking Type',
    'unlayer.tool.form.field.placeholder': 'Add a field',
    'unlayer.tool.form.fields.familyName': 'Last Name',
    'unlayer.tool.form.fields.givenName': 'First Name',
    'unlayer.tool.form.fields.group.fields.title': 'Fields',
    'unlayer.tool.form.fields.standard.addAsCustomField.label': 'Add as custom field',
    'unlayer.tool.form.fields.standard.addAsCustomField.placeholder': 'Enter your add as custom field',
    'unlayer.tool.form.fields.standard.address1.label': 'Address 1',
    'unlayer.tool.form.fields.standard.address1.placeholder': 'Enter your address 1',
    'unlayer.tool.form.fields.standard.address2.label': 'Address 2',
    'unlayer.tool.form.fields.standard.address2.placeholder': 'Enter your address 2',
    'unlayer.tool.form.fields.standard.address2Street1.label': 'Shipping address line 1',
    'unlayer.tool.form.fields.standard.address2Street1.placeholder': 'Enter your shipping address line 1',
    'unlayer.tool.form.fields.standard.address2Street2.label': 'Shipping address line 2',
    'unlayer.tool.form.fields.standard.address2Street2.placeholder': 'Enter your shipping address line 2',
    'unlayer.tool.form.fields.standard.address3Street1.label': 'Optional address line 1',
    'unlayer.tool.form.fields.standard.address3Street1.placeholder': 'Enter your optional address line 1',
    'unlayer.tool.form.fields.standard.address3Street2.label': 'Optional address line 2',
    'unlayer.tool.form.fields.standard.address3Street2.placeholder': 'Enter your optional address line 2',
    'unlayer.tool.form.fields.standard.anniversary.label': 'Anniversary',
    'unlayer.tool.form.fields.standard.anniversary.placeholder': 'Enter your anniversary',
    'unlayer.tool.form.fields.standard.anniversaryMonth.label': 'Anniversary month',
    'unlayer.tool.form.fields.standard.anniversaryMonth.placeholder': 'Enter your anniversary month',
    'unlayer.tool.form.fields.standard.anniversaryYear.label': 'Anniversary year',
    'unlayer.tool.form.fields.standard.anniversaryYear.placeholder': 'Enter your anniversary year',
    'unlayer.tool.form.fields.standard.assistantName.label': 'Assistant name',
    'unlayer.tool.form.fields.standard.assistantName.placeholder': 'Enter your assistant name',
    'unlayer.tool.form.fields.standard.assistantPhone.label': 'Assistant phone',
    'unlayer.tool.form.fields.standard.assistantPhone.placeholder': 'Enter your assistant phone',
    'unlayer.tool.form.fields.standard.billingAddressCountry.label': 'Billing address country',
    'unlayer.tool.form.fields.standard.billingAddressCountry.placeholder': 'Enter your billing address country',
    'unlayer.tool.form.fields.standard.billingAddressLine1.label': 'Billing address line 1',
    'unlayer.tool.form.fields.standard.billingAddressLine1.placeholder': 'Enter your billing address line 1',
    'unlayer.tool.form.fields.standard.billingAddressLine2.label': 'Billing address line 2',
    'unlayer.tool.form.fields.standard.billingAddressLine2.placeholder': 'Enter your billing address line 2',
    'unlayer.tool.form.fields.standard.billingAddressLocality.label': 'Billing address city',
    'unlayer.tool.form.fields.standard.billingAddressLocality.placeholder': 'Enter your billing address city',
    'unlayer.tool.form.fields.standard.billingAddressPostalCode.label': 'Billing address postal code',
    'unlayer.tool.form.fields.standard.billingAddressPostalCode.placeholder': 'Enter your billing address postal code',
    'unlayer.tool.form.fields.standard.billingAddressRegion.label': 'Billing address state',
    'unlayer.tool.form.fields.standard.billingAddressRegion.placeholder': 'Enter your billing address state',
    'unlayer.tool.form.fields.standard.billingAddressStreet.label': 'Billing address street',
    'unlayer.tool.form.fields.standard.billingAddressStreet.placeholder': 'Enter your billing address street',
    'unlayer.tool.form.fields.standard.birthday.label': 'Birthday',
    'unlayer.tool.form.fields.standard.birthday.placeholder': 'Enter your birthday',
    'unlayer.tool.form.fields.standard.birthdayMonth.label': 'Birthday month',
    'unlayer.tool.form.fields.standard.birthdayMonth.placeholder': 'Enter your birthday month',
    'unlayer.tool.form.fields.standard.birthdayYear.label': 'Birthday year',
    'unlayer.tool.form.fields.standard.birthdayYear.placeholder': 'Enter your birthday year',
    'unlayer.tool.form.fields.standard.business.label': 'Business name',
    'unlayer.tool.form.fields.standard.business.placeholder': 'Enter your business name',
    'unlayer.tool.form.fields.standard.businessEmail.label': 'Business email',
    'unlayer.tool.form.fields.standard.businessEmail.placeholder': 'Enter your business email address',
    'unlayer.tool.form.fields.standard.city.label': 'Billing address city',
    'unlayer.tool.form.fields.standard.city.placeholder': 'Enter your billing address city',
    'unlayer.tool.form.fields.standard.city2.label': 'Shipping address city',
    'unlayer.tool.form.fields.standard.city2.placeholder': 'Enter your shipping address city',
    'unlayer.tool.form.fields.standard.city3.label': 'Optional address city',
    'unlayer.tool.form.fields.standard.city3.placeholder': 'Enter your optional address city',
    'unlayer.tool.form.fields.standard.company.placeholder': 'Enter your company',
    'unlayer.tool.form.fields.standard.confirmPassword.label': 'Confirm password',
    'unlayer.tool.form.fields.standard.confirmPassword.placeholder': 'Enter your confirm password',
    'unlayer.tool.form.fields.standard.contactType.label': 'Contact type (Lead, Client, Other)',
    'unlayer.tool.form.fields.standard.contactType.placeholder': 'Enter your contact type (lead, client, other)',
    'unlayer.tool.form.fields.standard.country.label': 'Billing address country',
    'unlayer.tool.form.fields.standard.country.placeholder': 'Enter your billing address country',
    'unlayer.tool.form.fields.standard.country2.label': 'Shipping address country',
    'unlayer.tool.form.fields.standard.country2.placeholder': 'Enter your shipping address country',
    'unlayer.tool.form.fields.standard.country3.label': 'Optional address country',
    'unlayer.tool.form.fields.standard.country3.placeholder': 'Enter your optional address country',
    'unlayer.tool.form.fields.standard.countryCode.label': 'Country',
    'unlayer.tool.form.fields.standard.countryCode.placeholder': 'Enter your country',
    'unlayer.tool.form.fields.standard.dateCreated.label': 'Contact added to {productName}',
    'unlayer.tool.form.fields.standard.dateCreated.placeholder': 'Enter your contact added to {productname}',
    'unlayer.tool.form.fields.standard.daveRamsey.label': 'Dave Ramsey/EntreLeadership',
    'unlayer.tool.form.fields.standard.daveRamsey.placeholder': 'Enter your dave ramsey/entreleadership',
    'unlayer.tool.form.fields.standard.doNotImport.label': 'Do not import',
    'unlayer.tool.form.fields.standard.doNotImport.placeholder': 'Enter your do not import',
    'unlayer.tool.form.fields.standard.email.label': 'Email',
    'unlayer.tool.form.fields.standard.email.placeholder': 'Enter your email address',
    'unlayer.tool.form.fields.standard.email2.label': 'Email 2',
    'unlayer.tool.form.fields.standard.email2.placeholder': 'Enter your email address 2',
    'unlayer.tool.form.fields.standard.email3.label': 'Email 3',
    'unlayer.tool.form.fields.standard.email3.placeholder': 'Enter your email address 3',
    'unlayer.tool.form.fields.standard.emailAddress2.label': 'Email address 2',
    'unlayer.tool.form.fields.standard.emailAddress2.placeholder': 'Enter your email address 2',
    'unlayer.tool.form.fields.standard.emailAddress3.label': 'Email address 3',
    'unlayer.tool.form.fields.standard.emailAddress3.placeholder': 'Enter your email address 3',
    'unlayer.tool.form.fields.standard.extension.label': 'Ext',
    'unlayer.tool.form.fields.standard.extension.placeholder': 'Enter your ext',
    'unlayer.tool.form.fields.standard.facebook.label': 'Facebook',
    'unlayer.tool.form.fields.standard.facebook.placeholder': 'Enter your facebook',
    'unlayer.tool.form.fields.standard.fallbackViewLocale.label': 'Fallback Locale',
    'unlayer.tool.form.fields.standard.fallbackViewLocale.placeholder': 'Enter your fallback locale',
    'unlayer.tool.form.fields.standard.familyName.label': 'Last name',
    'unlayer.tool.form.fields.standard.familyName.placeholder': 'Enter your last name',
    'unlayer.tool.form.fields.standard.fax.label': 'Fax',
    'unlayer.tool.form.fields.standard.fax.placeholder': 'Enter your fax',
    'unlayer.tool.form.fields.standard.fax1.label': 'Fax',
    'unlayer.tool.form.fields.standard.fax1.placeholder': 'Enter your fax',
    'unlayer.tool.form.fields.standard.fax1Type.label': 'Fax 1 type',
    'unlayer.tool.form.fields.standard.fax1Type.placeholder': 'Enter your fax 1 type',
    'unlayer.tool.form.fields.standard.fax2.label': 'Fax 2',
    'unlayer.tool.form.fields.standard.fax2.placeholder': 'Enter your fax 2',
    'unlayer.tool.form.fields.standard.fax2Type.label': 'Fax 2 type',
    'unlayer.tool.form.fields.standard.fax2Type.placeholder': 'Enter your fax 2 type',
    'unlayer.tool.form.fields.standard.firstName.label': 'First name',
    'unlayer.tool.form.fields.standard.firstName.placeholder': 'Enter your first name',
    'unlayer.tool.form.fields.standard.friend.label': 'Friend',
    'unlayer.tool.form.fields.standard.friend.placeholder': 'Enter your friend',
    'unlayer.tool.form.fields.standard.fullName.label': 'Full name',
    'unlayer.tool.form.fields.standard.fullName.placeholder': 'Enter your full name',
    'unlayer.tool.form.fields.standard.givenName.label': 'First name',
    'unlayer.tool.form.fields.standard.givenName.placeholder': 'Enter your first name',
    'unlayer.tool.form.fields.standard.google.label': 'Google',
    'unlayer.tool.form.fields.standard.google.placeholder': 'Enter your google',
    'unlayer.tool.form.fields.standard.instagram.label': 'Instagram',
    'unlayer.tool.form.fields.standard.instagram.placeholder': 'Enter your instagram',
    'unlayer.tool.form.fields.standard.jobTitle.label': 'Job title',
    'unlayer.tool.form.fields.standard.jobTitle.placeholder': 'Enter your job title',
    'unlayer.tool.form.fields.standard.language.label': 'Language',
    'unlayer.tool.form.fields.standard.language.placeholder': 'Enter your language',
    'unlayer.tool.form.fields.standard.lastEmailed.label': 'Sent these contacts an email',
    'unlayer.tool.form.fields.standard.lastEmailed.placeholder': 'Enter your sent these contacts an email',
    'unlayer.tool.form.fields.standard.lastName.label': 'Last name',
    'unlayer.tool.form.fields.standard.lastName.placeholder': 'Enter your last name',
    'unlayer.tool.form.fields.standard.lastUpdated.label': 'Last updated',
    'unlayer.tool.form.fields.standard.lastUpdated.placeholder': 'Enter your last updated',
    'unlayer.tool.form.fields.standard.leadScore.label': 'Lead score',
    'unlayer.tool.form.fields.standard.leadScore.placeholder': 'Enter your lead score',
    'unlayer.tool.form.fields.standard.leadSource.label': 'Lead source',
    'unlayer.tool.form.fields.standard.leadSource.placeholder': 'Enter your lead source',
    'unlayer.tool.form.fields.standard.leadscore.label': 'Lead score',
    'unlayer.tool.form.fields.standard.leadscore.placeholder': 'Enter your lead score',
    'unlayer.tool.form.fields.standard.linkedin.label': 'LinkedIn',
    'unlayer.tool.form.fields.standard.linkedin.placeholder': 'Enter your linkedin',
    'unlayer.tool.form.fields.standard.locale.label': 'Locale',
    'unlayer.tool.form.fields.standard.locale.placeholder': 'Enter your locale',
    'unlayer.tool.form.fields.standard.locality.label': 'City',
    'unlayer.tool.form.fields.standard.locality.placeholder': 'Enter your city',
    'unlayer.tool.form.fields.standard.middleName.label': 'Middle name',
    'unlayer.tool.form.fields.standard.middleName.placeholder': 'Enter your middle name',
    'unlayer.tool.form.fields.standard.name.label': 'Name',
    'unlayer.tool.form.fields.standard.name.placeholder': 'Enter your name',
    'unlayer.tool.form.fields.standard.nickname.label': 'Nickname',
    'unlayer.tool.form.fields.standard.nickname.placeholder': 'Enter your nickname',
    'unlayer.tool.form.fields.standard.originDate.label': 'IP Address Captured On',
    'unlayer.tool.form.fields.standard.originDate.placeholder': 'Enter your ip address captured on',
    'unlayer.tool.form.fields.standard.originIpAddress.label': 'IP Address',
    'unlayer.tool.form.fields.standard.originIpAddress.placeholder': 'Enter your ip address',
    'unlayer.tool.form.fields.standard.other.label': 'Other',
    'unlayer.tool.form.fields.standard.other.placeholder': 'Enter your other',
    'unlayer.tool.form.fields.standard.otherAddressCountry.label': 'Other address country',
    'unlayer.tool.form.fields.standard.otherAddressCountry.placeholder': 'Enter your other address country',
    'unlayer.tool.form.fields.standard.otherAddressLine1.label': 'Other address line 1',
    'unlayer.tool.form.fields.standard.otherAddressLine1.placeholder': 'Enter your other address line 1',
    'unlayer.tool.form.fields.standard.otherAddressLine2.label': 'Other address line 2',
    'unlayer.tool.form.fields.standard.otherAddressLine2.placeholder': 'Enter your other address line 2',
    'unlayer.tool.form.fields.standard.otherAddressLocality.label': 'Other address city',
    'unlayer.tool.form.fields.standard.otherAddressLocality.placeholder': 'Enter your other address city',
    'unlayer.tool.form.fields.standard.otherAddressPostalCode.label': 'Other address postal code',
    'unlayer.tool.form.fields.standard.otherAddressPostalCode.placeholder': 'Enter your other address postal code',
    'unlayer.tool.form.fields.standard.otherAddressRegion.label': 'Other address state',
    'unlayer.tool.form.fields.standard.otherAddressRegion.placeholder': 'Enter your other address state',
    'unlayer.tool.form.fields.standard.otherAddressStreet.label': 'Other address street',
    'unlayer.tool.form.fields.standard.otherAddressStreet.placeholder': 'Enter your other address street',
    'unlayer.tool.form.fields.standard.owner.label': 'Owner',
    'unlayer.tool.form.fields.standard.owner.placeholder': 'Enter your owner',
    'unlayer.tool.form.fields.standard.partner.label': '{productName} Partner',
    'unlayer.tool.form.fields.standard.partner.placeholder': 'Enter your {productname} partner',
    'unlayer.tool.form.fields.standard.password.label': 'Password',
    'unlayer.tool.form.fields.standard.password.placeholder': 'Enter your password',
    'unlayer.tool.form.fields.standard.phone.label': 'Phone',
    'unlayer.tool.form.fields.standard.phone.placeholder': 'Enter your phone',
    'unlayer.tool.form.fields.standard.phone1.label': 'Phone 1',
    'unlayer.tool.form.fields.standard.phone1.placeholder': 'Enter your phone 1',
    'unlayer.tool.form.fields.standard.phone1Ext.label': 'Phone 1 ext',
    'unlayer.tool.form.fields.standard.phone1Ext.placeholder': 'Enter your phone 1 ext',
    'unlayer.tool.form.fields.standard.phone1Type.label': 'Phone 1 type',
    'unlayer.tool.form.fields.standard.phone1Type.placeholder': 'Enter your phone 1 type',
    'unlayer.tool.form.fields.standard.phone2.label': 'Phone 2',
    'unlayer.tool.form.fields.standard.phone2.placeholder': 'Enter your phone 2',
    'unlayer.tool.form.fields.standard.phone2Ext.label': 'Phone 2 ext',
    'unlayer.tool.form.fields.standard.phone2Ext.placeholder': 'Enter your phone 2 ext',
    'unlayer.tool.form.fields.standard.phone2Type.label': 'Phone 2 type',
    'unlayer.tool.form.fields.standard.phone2Type.placeholder': 'Enter your phone 2 type',
    'unlayer.tool.form.fields.standard.phone3.label': 'Phone 3',
    'unlayer.tool.form.fields.standard.phone3.placeholder': 'Enter your phone 3',
    'unlayer.tool.form.fields.standard.phone3Type.label': 'Phone 3 type',
    'unlayer.tool.form.fields.standard.phone3Type.placeholder': 'Enter your phone 3 type',
    'unlayer.tool.form.fields.standard.phone4.label': 'Phone 4',
    'unlayer.tool.form.fields.standard.phone4.placeholder': 'Enter your phone 4',
    'unlayer.tool.form.fields.standard.phone4Type.label': 'Phone 4 type',
    'unlayer.tool.form.fields.standard.phone4Type.placeholder': 'Enter your phone 4 type',
    'unlayer.tool.form.fields.standard.phone5.label': 'Phone 5',
    'unlayer.tool.form.fields.standard.phone5.placeholder': 'Enter your phone 5',
    'unlayer.tool.form.fields.standard.phone5Type.label': 'Phone 5 type',
    'unlayer.tool.form.fields.standard.phone5Type.placeholder': 'Enter your phone 5 type',
    'unlayer.tool.form.fields.standard.phoneNumber.label': 'Phone number',
    'unlayer.tool.form.fields.standard.phoneNumber.placeholder': 'Enter your phone number',
    'unlayer.tool.form.fields.standard.phoneType.label': 'Phone type',
    'unlayer.tool.form.fields.standard.phoneType.placeholder': 'Enter your phone type',
    'unlayer.tool.form.fields.standard.postalCode.label': 'Billing address postal code',
    'unlayer.tool.form.fields.standard.postalCode.placeholder': 'Enter your billing address postal code',
    'unlayer.tool.form.fields.standard.postalCode2.label': 'Shipping address postal code',
    'unlayer.tool.form.fields.standard.postalCode2.placeholder': 'Enter your shipping address postal code',
    'unlayer.tool.form.fields.standard.postalCode3.label': 'Optional address postal code',
    'unlayer.tool.form.fields.standard.postalCode3.placeholder': 'Enter your optional address postal code',
    'unlayer.tool.form.fields.standard.postalCodeGeneric.label': 'Postal code',
    'unlayer.tool.form.fields.standard.postalCodeGeneric.placeholder': 'Enter your postal code',
    'unlayer.tool.form.fields.standard.region.label': 'State',
    'unlayer.tool.form.fields.standard.region.placeholder': 'Enter your state',
    'unlayer.tool.form.fields.standard.reviewSite.label': 'Review Site',
    'unlayer.tool.form.fields.standard.reviewSite.placeholder': 'Enter your review site',
    'unlayer.tool.form.fields.standard.role.label': 'Role',
    'unlayer.tool.form.fields.standard.role.placeholder': 'Enter your role',
    'unlayer.tool.form.fields.standard.selectAnOption.label': 'Select an option',
    'unlayer.tool.form.fields.standard.selectAnOption.placeholder': 'Enter your select an option',
    'unlayer.tool.form.fields.standard.shippingAddressCountry.label': 'Shipping address country',
    'unlayer.tool.form.fields.standard.shippingAddressCountry.placeholder': 'Enter your shipping address country',
    'unlayer.tool.form.fields.standard.shippingAddressLine1.label': 'Shipping address line 1',
    'unlayer.tool.form.fields.standard.shippingAddressLine1.placeholder': 'Enter your shipping address line 1',
    'unlayer.tool.form.fields.standard.shippingAddressLine2.label': 'Shipping address line 2',
    'unlayer.tool.form.fields.standard.shippingAddressLine2.placeholder': 'Enter your shipping address line 2',
    'unlayer.tool.form.fields.standard.shippingAddressLocality.label': 'Shipping address city',
    'unlayer.tool.form.fields.standard.shippingAddressLocality.placeholder': 'Enter your shipping address city',
    'unlayer.tool.form.fields.standard.shippingAddressPostalCode.label': 'Shipping address postal code',
    'unlayer.tool.form.fields.standard.shippingAddressPostalCode.placeholder': 'Enter your shipping address postal code',
    'unlayer.tool.form.fields.standard.shippingAddressRegion.label': 'Shipping address state',
    'unlayer.tool.form.fields.standard.shippingAddressRegion.placeholder': 'Enter your shipping address state',
    'unlayer.tool.form.fields.standard.shippingAddressStreet.label': 'Shipping address street',
    'unlayer.tool.form.fields.standard.shippingAddressStreet.placeholder': 'Enter your shipping address street',
    'unlayer.tool.form.fields.standard.spouseName.label': 'Spouse name',
    'unlayer.tool.form.fields.standard.spouseName.placeholder': 'Enter your spouse name',
    'unlayer.tool.form.fields.standard.ssn.label': 'SSN',
    'unlayer.tool.form.fields.standard.ssn.placeholder': 'Enter your ssn',
    'unlayer.tool.form.fields.standard.state.label': 'Billing address state',
    'unlayer.tool.form.fields.standard.state.placeholder': 'Enter your billing address state',
    'unlayer.tool.form.fields.standard.state2.label': 'Shipping address state',
    'unlayer.tool.form.fields.standard.state2.placeholder': 'Enter your shipping address state',
    'unlayer.tool.form.fields.standard.state3.label': 'Optional address state',
    'unlayer.tool.form.fields.standard.state3.placeholder': 'Enter your optional address state',
    'unlayer.tool.form.fields.standard.street1.label': 'Street address 1',
    'unlayer.tool.form.fields.standard.street1.placeholder': 'Enter your street address 1',
    'unlayer.tool.form.fields.standard.street2.label': 'Street address 2',
    'unlayer.tool.form.fields.standard.street2.placeholder': 'Enter your street address 2',
    'unlayer.tool.form.fields.standard.streetAddress1.label': 'Billing address line 1',
    'unlayer.tool.form.fields.standard.streetAddress1.placeholder': 'Enter your billing address line 1',
    'unlayer.tool.form.fields.standard.streetAddress2.label': 'Billing address line 2',
    'unlayer.tool.form.fields.standard.streetAddress2.placeholder': 'Enter your billing address line 2',
    'unlayer.tool.form.fields.standard.suffix.label': 'Suffix',
    'unlayer.tool.form.fields.standard.suffix.placeholder': 'Enter your suffix',
    'unlayer.tool.form.fields.standard.tag.label': 'Tags: Create tags from field values',
    'unlayer.tool.form.fields.standard.tag.placeholder': 'Enter your tags: create tags from field values',
    'unlayer.tool.form.fields.standard.tags.label': 'Tags',
    'unlayer.tool.form.fields.standard.tags.placeholder': 'Enter your tags',
    'unlayer.tool.form.fields.standard.timeZone.label': 'Time zone',
    'unlayer.tool.form.fields.standard.timeZone.placeholder': 'Enter your time zone',
    'unlayer.tool.form.fields.standard.title.label': 'Title',
    'unlayer.tool.form.fields.standard.title.placeholder': 'Enter your title',
    'unlayer.tool.form.fields.standard.twitter.label': 'Twitter',
    'unlayer.tool.form.fields.standard.twitter.placeholder': 'Enter your twitter',
    'unlayer.tool.form.fields.standard.type.label': 'Type',
    'unlayer.tool.form.fields.standard.type.placeholder': 'Enter your type',
    'unlayer.tool.form.fields.standard.uncategorized.label': 'Uncategorized',
    'unlayer.tool.form.fields.standard.uncategorized.placeholder': 'Enter your uncategorized',
    'unlayer.tool.form.fields.standard.username.label': 'Username',
    'unlayer.tool.form.fields.standard.username.placeholder': 'Enter your username',
    'unlayer.tool.form.fields.standard.website.label': 'Website',
    'unlayer.tool.form.fields.standard.website.placeholder': 'Enter your website',
    'unlayer.tool.form.fields.standard.youtube.label': 'YouTube',
    'unlayer.tool.form.fields.standard.youtube.placeholder': 'Enter your youtube',
    'unlayer.tool.form.label': 'Form',
    'unlayer.tool.form.name.label': 'Form Name',
    'unlayer.tool.form.name.placeholder': 'Enter form name',
    'unlayer.tool.properties.backgroundColor': 'Background Color',
    'unlayer.tool.properties.border': 'Border',
    'unlayer.tool.properties.button': 'Button',
    'unlayer.tool.properties.buttonTextLabel': 'Text',
    'unlayer.tool.properties.color': 'Color',
    'unlayer.tool.properties.fontSize': 'Font Size',
    'unlayer.tool.properties.formAlignment': 'Form Alignment',
    'unlayer.tool.properties.formPosition': 'Form Position',
    'unlayer.tool.properties.formWidth': 'Form Width',
    'unlayer.tool.properties.labels': 'Labels',
    'unlayer.tool.properties.layout': 'Layout',
    'unlayer.tool.properties.margin': 'Margin',
    'unlayer.tool.properties.padding': 'Padding',
    'unlayer.tool.properties.roundedBorder': 'Rounded Border',
    'unlayer.tool.properties.spaceBetweenFields': 'Space Between Fields',
    'unlayer.tool.properties.stackOnMobile': 'Stack on Mobile',
    'unlayer.tool.properties.textColor': 'Text Color',
    'unlayer.tool.properties.width': 'Width',
    'unlayer.tool.sharedOpts.layout': 'Layout',
    'LandingPageFormSubmitted/description': 'Notify yourself or your employees about any new landing page submissions. Plus, send a confirmation email to your contact.',
    'LandingPageFormSubmitted/goal': 'Get notified of new leads',
    'LandingPageFormSubmitted/step': ' send (Landing page follow-up) email and (New landing page lead) notification',
    'LandingPageFormSubmitted/step1.body': "<p>Hello [[contact.first_name]],</p><p>Thank you for contacting us at {{profile.company.name}}. I'll be in touch soon to learn more about your needs. </p><p><span style=\"color: rgb(0, 0, 0);\">In the meantime, please reply to this email with any additional questions you may have.</span></p><p>Kind regards,</p>",
    'LandingPageFormSubmitted/step1.name': 'Send (Landing page follow-up) email',
    'LandingPageFormSubmitted/step1.subject': 'Thanks for reaching out, [[contact.first_name]]',
    'LandingPageFormSubmitted/step1.title': 'Landing page follow-up',
    'LandingPageFormSubmitted/step2.contentText': '[[contact.first_name]] filled out a landing page form!',
    'LandingPageFormSubmitted/step2.contentTitle': 'New landing page lead',
    'LandingPageFormSubmitted/step2.emailContent': '<p>[[contact.first_name]] filled out a landing page form! </p><p>We sent an email confirmation to them at [[contact.email1.address]].</p>',
    'LandingPageFormSubmitted/step2.name': 'Send (New landing page lead) notification',
    'LandingPageFormSubmitted/title': 'Landing page leads',
    'LandingPageFormSubmitted/trigger': ' any landing page form is submitted',
    'LandingPageFormSubmittedTrigger/anyLandingPageFormSubmitted.description': 'When any landing page form is submitted',
    'LandingPageFormSubmittedTrigger/anyLandingPageFormSubmitted.name': 'Any landing page form is submitted',
    'LandingPageFormSubmittedTrigger/anyLandingPageFormSubmitted.title': 'Any landing page form',
    'LandingPageFormSubmittedTrigger/buttonText': 'New landing page',
    'LandingPageFormSubmittedTrigger/createForm.confirm': 'Go to landing page builder',
    'LandingPageFormSubmittedTrigger/createForm.message': 'A draft will be saved of your current automation.',
    'LandingPageFormSubmittedTrigger/createForm.title': 'Navigate to the landing page builder to add a new form?',
    'LandingPageFormSubmittedTrigger/emptyLandingPageForms': 'None available',
    'LandingPageFormSubmittedTrigger/emptySearch': 'No results found for "{search}"',
    'LandingPageFormSubmittedTrigger/formsHeader': 'Or select a specific form',
    'LandingPageFormSubmittedTrigger/header': 'Choose a form',
    'LandingPageFormSubmittedTrigger/landingPageFormSubmitted': 'Landing page form is submitted',
    'LandingPageFormSubmittedTrigger/search': 'Search landing pages',
    'LandingPageFormSubmittedTrigger/singleLandingPageFormSubmitted': 'landing page form ({id}) is submitted',
    'landingPagesCustomForms.createForm': 'Create form',
    'landingPagesCustomForms.editForm': 'Edit form',
    'landingPagesCustomForms.fieldGroups.Additional': 'Additional info',
    'landingPagesCustomForms.fieldGroups.Addresses': 'Addresses',
    'landingPagesCustomForms.fieldGroups.BillingAddress': 'Billing address',
    'landingPagesCustomForms.fieldGroups.CompositeFields': 'Grouped fields',
    'landingPagesCustomForms.fieldGroups.ContactFields': 'Contact fields',
    'landingPagesCustomForms.fieldGroups.CustomFields': 'Custom fields',
    'landingPagesCustomForms.fieldGroups.FormOnlyFields': 'Form fields',
    'landingPagesCustomForms.fieldGroups.General': 'General',
    'landingPagesCustomForms.fieldGroups.Internal': 'Internal',
    'landingPagesCustomForms.fieldGroups.OptionalAddress': 'Optional address',
    'landingPagesCustomForms.fieldGroups.PhoneAndEmail': 'Phone and email',
    'landingPagesCustomForms.fieldGroups.ShippingAddress': 'Shipping address',
    'landingPagesCustomForms.fieldGroups.Social': 'Social',
    'landingPagesCustomForms.fields.standard.anniversary': 'Anniversary',
    'landingPagesCustomForms.fields.standard.billingAddress.countryCode': 'Billing country',
    'landingPagesCustomForms.fields.standard.billingAddress.extendedAddress': 'Billing address 2',
    'landingPagesCustomForms.fields.standard.billingAddress.locality': 'Billing city',
    'landingPagesCustomForms.fields.standard.billingAddress.postalCode': 'Billing postal code',
    'landingPagesCustomForms.fields.standard.billingAddress.region': 'Billing state',
    'landingPagesCustomForms.fields.standard.billingAddress.streetAddress': 'Billing address',
    'landingPagesCustomForms.fields.standard.billingAddressGroup': 'Billing address',
    'landingPagesCustomForms.fields.standard.birthDate': 'Birthday',
    'landingPagesCustomForms.fields.standard.contactType': 'Contact type',
    'landingPagesCustomForms.fields.standard.creditCard': 'Payment fields',
    'landingPagesCustomForms.fields.standard.companyName': 'Company',
    'landingPagesCustomForms.fields.standard.emailAddress1': 'Email address',
    'landingPagesCustomForms.fields.standard.emailAddress2': 'Email address 2',
    'landingPagesCustomForms.fields.standard.emailAddress3': 'Email address 3',
    'landingPagesCustomForms.fields.standard.facebook': 'Facebook',
    'landingPagesCustomForms.fields.standard.familyName': 'Last name',
    'landingPagesCustomForms.fields.standard.faxGroup1': 'Fax 1',
    'landingPagesCustomForms.fields.standard.faxGroup2': 'Fax 2',
    'landingPagesCustomForms.fields.standard.faxNumber1.type': 'Fax type',
    'landingPagesCustomForms.fields.standard.faxNumber1.value': 'Fax',
    'landingPagesCustomForms.fields.standard.faxNumber2.type': 'Fax 2 type',
    'landingPagesCustomForms.fields.standard.faxNumber2.value': 'Fax 2',
    'landingPagesCustomForms.fields.standard.givenName': 'First name',
    'landingPagesCustomForms.fields.standard.jobTitle': 'Job title',
    'landingPagesCustomForms.fields.standard.linkedIn': 'LinkedIn',
    'landingPagesCustomForms.fields.standard.middleName': 'Middle name',
    'landingPagesCustomForms.fields.standard.note': 'Add note',
    'landingPagesCustomForms.fields.standard.optionalAddressGroup': 'Other address',
    'landingPagesCustomForms.fields.standard.otherAddress.countryCode': 'Optional country',
    'landingPagesCustomForms.fields.standard.otherAddress.extendedAddress': 'Optional address 2',
    'landingPagesCustomForms.fields.standard.otherAddress.locality': 'Optional city',
    'landingPagesCustomForms.fields.standard.otherAddress.postalCode': 'Optional postal code',
    'landingPagesCustomForms.fields.standard.otherAddress.region': 'Optional state',
    'landingPagesCustomForms.fields.standard.otherAddress.streetAddress': 'Optional address',
    'landingPagesCustomForms.fields.standard.owner': 'Owner',
    'landingPagesCustomForms.fields.standard.phoneGroup1': 'Phone',
    'landingPagesCustomForms.fields.standard.phoneGroup2': 'Phone 2',
    'landingPagesCustomForms.fields.standard.phoneGroup3': 'Phone 3',
    'landingPagesCustomForms.fields.standard.phoneGroup4': 'Phone 4',
    'landingPagesCustomForms.fields.standard.phoneGroup5': 'Phone 5',
    'landingPagesCustomForms.fields.standard.phoneNumber1.type': 'Phone type',
    'landingPagesCustomForms.fields.standard.phoneNumber1.value': 'Phone',
    'landingPagesCustomForms.fields.standard.phoneNumber2.type': 'Phone 2 type',
    'landingPagesCustomForms.fields.standard.phoneNumber2.value': 'Phone 2',
    'landingPagesCustomForms.fields.standard.phoneNumber3.type': 'Phone 3 type',
    'landingPagesCustomForms.fields.standard.phoneNumber3.value': 'Phone 3',
    'landingPagesCustomForms.fields.standard.phoneNumber4.type': 'Phone 4 type',
    'landingPagesCustomForms.fields.standard.phoneNumber4.value': 'Phone 4',
    'landingPagesCustomForms.fields.standard.phoneNumber5.type': 'Phone 5 type',
    'landingPagesCustomForms.fields.standard.phoneNumber5.value': 'Phone 5',
    'landingPagesCustomForms.fields.standard.preferredName': 'Nickname',
    'landingPagesCustomForms.fields.standard.shippingAddress.countryCode': 'Shipping country',
    'landingPagesCustomForms.fields.standard.shippingAddress.extendedAddress': 'Shipping address 2',
    'landingPagesCustomForms.fields.standard.shippingAddress.locality': 'Shipping city',
    'landingPagesCustomForms.fields.standard.shippingAddress.postalCode': 'Shipping postal code',
    'landingPagesCustomForms.fields.standard.shippingAddress.region': 'Shipping state',
    'landingPagesCustomForms.fields.standard.shippingAddress.streetAddress': 'Shipping address',
    'landingPagesCustomForms.fields.standard.shippingAddressGroup': 'Shipping address',
    'landingPagesCustomForms.fields.standard.spouseName': 'Spouse name',
    'landingPagesCustomForms.fields.standard.suffix': 'Suffix',
    'landingPagesCustomForms.fields.standard.tag': 'Add tags',
    'landingPagesCustomForms.fields.standard.title': 'Title',
    'landingPagesCustomForms.fields.standard.twitter': 'Twitter',
    'landingPagesCustomForms.fields.standard.website': 'Website',
    'landingPagesCustomForms.internal.form': 'Internal form',
    'landingPagesCustomForms.internalFull': 'Internal forms',
    'landingPagesCustomForms.internalTitle': 'Internal forms',
    'landingPagesCustomForms.public.form': 'Public form',
    'landingPagesCustomForms.publicTitle': 'Public forms',
    'BroadcastEditor/ehawkModalTitle': 'Before you send broadcast emails, tell us about your business',
    'BroadcastEditor/errors.loading': 'There was an error loading your broadcast. Please refresh the page to try again.',
    'BroadcastEditor/errors.noModify': 'You cannot modify this email.',
    'BroadcastEditor/errors.saving': 'There was an error saving your broadcast. Please refresh the page to try again.',
    'BroadcastEditor/errors.sending': 'There was an error sending your broadcast. Please refresh the page and try again.',
    'BroadcastEditor/errors.validation.contentRisk': 'Please fix content errors to continue.',
    'BroadcastEditor/errors.validation.multiple': 'There are multiple fields with errors. Please correct them to continue.',
    'BroadcastEditor/errors.validation.single.AUDIENCE': 'Please add an audience to continue.',
    'BroadcastEditor/errors.validation.single.CONTENT': 'Please add email content to continue.',
    'BroadcastEditor/errors.validation.single.SCHEDULE': 'Please schedule your broadcast to continue.',
    'BroadcastEditor/errors.validation.single.SENDER': 'Please add a sender to continue.',
    'BroadcastEditor/errors.validation.single.SUBJECT': 'Please add a subject to continue.',
    'BroadcastEditor/sectionTitle.AUDIENCE': 'Audience',
    'BroadcastEditor/sectionTitle.CONTENT': 'Content',
    'BroadcastEditor/sectionTitle.SCHEDULE': 'Schedule',
    'BroadcastEditor/sectionTitle.SENDER': 'Sender',
    'BroadcastEditor/sectionTitle.SUBJECT': 'Subject',
    'BroadcastEditor/timingOptions.rightNow.success': 'Your broadcast has been sent.',
    'BroadcastEditor/timingOptions.sendLater.success': 'Your broadcast is scheduled to be sent.',
    'BroadcastFilters/all': 'All',
    'BroadcastFilters/drafts': 'Drafts',
    'BroadcastFilters/filter': 'Filter',
    'BroadcastFilters/scheduled': 'Scheduled',
    'BroadcastFilters/sent': 'Sent',
    'BroadcastHeader/ehawkModalTitle': 'Before you send broadcast emails, tell us about your business',
    'BroadcastHeader/noSubject': '(No subject)',
    'BroadcastHeader/schedule': 'Schedule',
    'BroadcastHeader/scheduleBroadcast': 'Schedule broadcast',
    'BroadcastHeader/send': 'Send',
    'BroadcastHeader/sendBroadcast': 'Send broadcast',
    'BroadcastHeader/sendTest': 'Send a test',
    'BroadcastItem/broadcastDateMessage': '{action} on {date} at {time}',
    'BroadcastItem/broadcastErrorMessage': 'This broadcast encountered an error',
    'BroadcastItem/broadcastSendingMessage': 'Sending now',
    'BroadcastItem/clicks': 'Clicks',
    'BroadcastItem/deleteConfirmMessage': 'Do you really want to delete this draft?',
    'BroadcastItem/deleteConfirmTitle': 'Really delete?',
    'BroadcastItem/deleteErrorMessage': 'Unable to delete broadcast',
    'BroadcastItem/draftNoSubject': '(no subject)',
    'BroadcastItem/opens': 'Opens',
    'BroadcastItem/startBatchError': 'Unable to resume broadcast',
    'BroadcastItem/startBatchSuccess': 'Your broadcast has been resumed',
    'BroadcastItem/status.Cancelled': 'Cancelled',
    'BroadcastItem/status.Done': 'Sent',
    'BroadcastItem/status.Draft': 'Edited',
    'BroadcastItem/status.Processing': 'Sending in progress',
    'BroadcastItem/status.Queued': 'Sending',
    'BroadcastItem/statusChip.Cancelled': 'Cancelled by user',
    'BroadcastItem/statusChip.Done': 'Sent',
    'BroadcastItem/statusChip.Draft': 'Draft',
    'BroadcastItem/statusChip.Error': 'Error',
    'BroadcastItem/statusChip.Processing': 'Sending',
    'BroadcastItem/statusChip.Queued': 'Scheduled',
    'BroadcastItem/statusChip.Scheduling': 'Scheduling',
    'BroadcastItem/stopBatchError': 'Unable to pause broadcast',
    'BroadcastItem/stopBatchSuccess': 'Your broadcast has been paused',
    'BroadcastItem/testVariant': 'Test variant',
    'BroadcastItem/winner': 'Winner',
    'BroadcastOptions/cancel': 'Stop sending',
    'BroadcastOptions/copy': 'Make a copy',
    'BroadcastOptions/report': 'View report',
    'BroadcastOptions/reschedule': 'Reschedule',
    'BroadcastOptions/resume': 'Resume sending',
    'BroadcastPageActions/automatedSms.sendSmsName': 'Send text',
    'BroadcastPageActions/eHawkModalTitle': 'Before you create automations, tell us about your business',
    'BroadcastReportPage/emailPreview': 'Email preview',
    'BroadcastReportPage/previewError': 'Unable to display email preview',
    'BroadcastReportPage/splitTestHeading': 'A/B Results',
    'BroadcastReportPageHeaderActions/guideBody': "Email open rates are less accurate as a result of new industry privacy regulations, so they are hidden by default. If you'd like to display estimated open rates, you can from here.",
    'BroadcastReportPageHeaderActions/hideOpens': 'Hide opens',
    'BroadcastReportPageHeaderActions/showOpens': 'Show opens',
    'BroadcastTemplatePreview/useThisDraft': 'Use this draft',
    'BroadcastTemplatePreview/useThisTemplate': 'Use this template',
    'BroadcastTypeModal/cardDescriptionAdvanced': 'Select from a gallery of design-driven templates.',
    'BroadcastTypeModal/cardDescriptionSimple': 'Promote your business with content-focused broadcasts.',
    'BroadcastTypeModal/cardSubtitleAdvanced': '(Advanced Builder)',
    'BroadcastTypeModal/cardSubtitleSimple': '(Simple Builder)',
    'BroadcastTypeModal/cardTitleAdvanced': 'Text and graphics',
    'BroadcastTypeModal/cardTitleSimple': 'Text only',
    'BroadcastTypeModal/chooseHowEmailBroadcast': 'How do you want to create a legacy email broadcast?',
    'BroadcastTypeModal/eHawkModalTitle': 'Before you send broadcast emails, tell us about your business',
    'BroadcastTypeModal/modalTitle': 'Legacy builder',
    'BroadcastWizard/allSet': 'All set!',
    'BroadcastWizard/audienceStepTitle': 'Define your audience',
    'BroadcastWizard/bestTime': 'At the best time today',
    'BroadcastWizard/broadcasts': 'Broadcasts',
    'BroadcastWizard/canSpamWarning.actionLabel': 'Add business information',
    'BroadcastWizard/canSpamWarning.description': 'To comply with email law, add your address and other business info.',
    'BroadcastWizard/canSpamWarning.title': 'Add your business information',
    'BroadcastWizard/detailsStepTitle': 'Create your email content',
    'BroadcastWizard/eHawkModalTitle': 'Before you send broadcast emails, tell us about your business',
    'BroadcastWizard/loadingError': "Something went wrong and we couldn't load your broadcast. Please try again.",
    'BroadcastWizard/noContent': 'No email content',
    'BroadcastWizard/noRecipients': 'No recipients selected',
    'BroadcastWizard/noSubject': 'No subject line',
    'BroadcastWizard/recipients': 'unique recipient | unique recipients',
    'BroadcastWizard/rightAway': 'Right away',
    'BroadcastWizard/save': 'Your broadcast has been saved.',
    'BroadcastWizard/saveButton': 'Save and come back later',
    'BroadcastWizard/sendButton': 'Send broadcast',
    'BroadcastWizard/sendConfirmation.bestTimeMessage': 'This broadcast will be scheduled for the best time in the next 24 hours',
    'BroadcastWizard/sendConfirmation.confirm': 'Send broadcast',
    'BroadcastWizard/sendConfirmation.rightAwayMessage': 'This broadcast will be sent as soon as possible',
    'BroadcastWizard/sendConfirmation.specificTimeMessage': 'This broadcast will be scheduled for your selected time',
    'BroadcastWizard/sendConfirmation.title': 'Are you sure you want to send?',
    'BroadcastWizard/sendingError': "Something went wrong and we couldn't send your broadcast. Please try again.",
    'BroadcastWizard/specificTime': 'At a specific time',
    'BroadcastWizard/success': 'Your broadcast has been successfully sent!',
    'BroadcastWizard/timingStepTitle': 'Select your timing',
    'BroadcastWizard/title': 'New email broadcast',
    'BroadcastWizardPage/loadingError': "Something went wrong and we couldn't load your broadcast. Please try again.",
    'BroadcastsEmptyState/action': 'Create a broadcast',
    'BroadcastsEmptyState/description': "A broadcast is an email that's sent to many contacts at once. After you create and send a broadcast the results will be available here.",
    'BroadcastsEmptyState/title': "You haven't sent any email broadcasts yet",
    'BroadcastsFtuState/cta': 'Create a broadcast',
    'BroadcastsFtuState/description': 'Create and send targeted marketing emails that deliver your message with impact.',
    'BroadcastsFtuState/feature': 'Broadcasts',
    'BroadcastsFtuState/slides.1.guide': 'Use placeholder text in your subject line to make your broadcasts feel personal.',
    'BroadcastsFtuState/slides.1.title': 'Create personalized email content',
    'BroadcastsFtuState/slides.2.guide': 'Do you have email contact lists? You can add them here. You can add individual contacts, too.',
    'BroadcastsFtuState/slides.2.title': 'Target your content to the right audience',
    'BroadcastsFtuState/slides.3.guide': "You decide when the broadcast should send. It will be delivered based on each individual recipient's timezone.",
    'BroadcastsFtuState/slides.3.title': 'Choose the optimal time to send',
    'BroadcastsFtuState/slides.4.guide': 'At a glance, see how your broadcasts are performing, from delivery status to audience size.',
    'BroadcastsFtuState/slides.4.title': 'Track your performance',
    'BroadcastsFtuState/summary': 'Create and send emails to your contact lists',
    'BroadcastsFtuState/title': 'Keep your contacts engaged with broadcasts',
    'EmailBroadcastsPage/broadcasts': 'Broadcasts',
    'KmnConversationMessage/BROADCAST': 'Broadcast',
    'SidePanelAutomatedMessageItem/BROADCAST': 'Broadcast',
    'SidePanelAutomatedMessageThreadItem/BROADCAST': 'Broadcast',
    'SmsBroadcastItem/broadcastResumeSuccess': 'Broadcast resumed successfully',
    'SmsBroadcastItem/broadcastStopSuccess': 'Broadcast stopped successfully',
    'SmsBroadcastSummaryModal/broadcastResumeSuccess': 'Broadcast resumed successfully',
    'SmsBroadcastSummaryModal/broadcastStopSuccess': 'Broadcast stopped successfully',
    'SmsBroadcastSummaryModal/mobileTitle': 'Broadcast Details',
    'SmsBroadcastWriteStage/broadcastTitle': 'Broadcast name',
    'SplitTestSidebar/confirmResume': 'Broadcasts scheduled for a time in the past will be sent immediately.',
    'TextBroadcastsPage/broadcasts': 'Broadcasts',
    'nav.broadcasts': 'Broadcasts',
};
